// Admin service using Supabase Auth (SECURE - no service key)
// Authentication is handled by Supabase's built-in auth system
// RLS policies control data access based on authenticated user's role

import { supabase } from "../lib/supabase";
import { WebhookService } from "./webhookService";
import { SessionManager } from "./sessionManager";
import { clearCachedAdminIdentity, isTerminalSessionError } from "./adminSessionAccess";
import { buildNextModelOutputReview } from "../utils/modelReviewUtils";
import {
  buildDashboardSearchUnavailableError,
  createDashboardSearchResolution,
  getDashboardSearchContext,
  getDashboardSearchFilterState,
  getDashboardUsersSelect,
  normalizeDashboardPhoneSearchValue,
} from "./adminDashboardSearch";

// Helper function to parse products_offered which might be stored as a string with markdown
const parseProductsOffered = (productsOffered) => {
  if (!productsOffered) return null;

  const normalizeParsedValue = (value) => {
    if (!value) return null;

    // Unwrap array payloads like [{ products_offered: { checkout_page: ... } }]
    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      if (typeof value[0] === 'object') {
        return normalizeParsedValue(value[0]);
      }
      return value;
    }

    // Unwrap nested products_offered wrapper when present
    if (typeof value === 'object') {
      if (value.products_offered) {
        return normalizeParsedValue(value.products_offered);
      }
      return value;
    }

    // Handle stringified JSON (sometimes wrapped in markdown code fences)
    if (typeof value === 'string') {
      try {
        let cleaned = value.trim();
        cleaned = cleaned.replace(/^```json\s*/i, '');
        cleaned = cleaned.replace(/^```\s*/i, '');
        cleaned = cleaned.replace(/\s*```$/i, '');

        const parsed = JSON.parse(cleaned);
        return normalizeParsedValue(parsed);
      } catch (e) {
        console.warn('Failed to parse products_offered as JSON:', e);
        return null;
      }
    }

    return null;
  };

  return normalizeParsedValue(productsOffered);
};

const DASHBOARD_INTERFACE_RESULT_LIMIT = 500;

const ensureArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const normalizeConsultationTypes = (consultationType) => {
  if (!consultationType || typeof consultationType !== "string") {
    return ["Skin"];
  }

  const normalized = consultationType
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  return normalized.length > 0 ? normalized : ["Skin"];
};

const buildSensitivityDetails = (skinSensitivity, additionalSensitivity) => {
  const sensitivities = [];

  if (Array.isArray(skinSensitivity)) {
    skinSensitivity.forEach((item) => {
      if (typeof item === "string" && item.trim()) {
        sensitivities.push(item.trim());
      }
    });
  } else if (typeof skinSensitivity === "string" && skinSensitivity.trim()) {
    sensitivities.push(skinSensitivity.trim());
  }

  if (
    typeof additionalSensitivity === "string" &&
    additionalSensitivity.trim()
  ) {
    sensitivities.push(additionalSensitivity.trim());
  }

  return sensitivities.length > 0 ? sensitivities.join(", ") : "None";
};

const calculateProcessingTime = (startAt, endAt) => {
  if (!startAt || !endAt) return null;

  const startTime = new Date(startAt);
  const endTime = new Date(endAt);

  if (Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime())) {
    return null;
  }

  const diffMins = Math.floor((endTime - startTime) / 60000);
  return diffMins > 0 ? `${diffMins} min` : "< 1 min";
};

const normalizeImageValidationStatus = (consultation) => {
  const explicitStatus = String(
    consultation?.image_validation_status || ""
  ).trim().toLowerCase();

  if (
    explicitStatus === "pending" ||
    explicitStatus === "processing" ||
    explicitStatus === "valid" ||
    explicitStatus === "invalid" ||
    explicitStatus === "failed"
  ) {
    return explicitStatus;
  }

  if (consultation?.detailed_diagnosis === "Image not valid") {
    return "invalid";
  }

  if (
    consultation?.dermatologist_analysis ||
    (consultation?.detailed_diagnosis &&
      consultation.detailed_diagnosis !== "Image not valid")
  ) {
    return "valid";
  }

  return "pending";
};

const getImageAnalysisDetailsText = (consultation) => {
  const diagnosis =
    typeof consultation?.detailed_diagnosis === "string"
      ? consultation.detailed_diagnosis.trim()
      : "";

  if (!diagnosis || diagnosis === "Image not valid") {
    return null;
  }

  return diagnosis;
};

const deriveImageAnalysisDisplay = (consultation) => {
  const imageValidationStatus = normalizeImageValidationStatus(consultation);
  const detailsText = getImageAnalysisDetailsText(consultation);

  if (imageValidationStatus === "invalid") {
    return "Image not valid";
  }

  if (imageValidationStatus === "failed") {
    return "Validation failed";
  }

  if (imageValidationStatus === "valid") {
    return detailsText || "Validated";
  }

  return "Pending";
};

const formatAdminDateTime = (value) => {
  if (!value) return "N/A";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "N/A";

  return parsed.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const pickLatestByTimestamp = (items, timestampField) => {
  const values = ensureArray(items).filter(Boolean);
  if (values.length === 0) return null;

  return values
    .slice()
    .sort((left, right) => {
      const leftTime = new Date(left?.[timestampField] || 0).getTime();
      const rightTime = new Date(right?.[timestampField] || 0).getTime();
      return rightTime - leftTime;
    })[0];
};

const extractErrorMessage = (error) => {
  if (!error) return "Unknown error";
  if (typeof error === "string") return error;
  if (typeof error.message === "string" && error.message.trim()) {
    return error.message.trim();
  }
  if (typeof error.details === "string" && error.details.trim()) {
    return error.details.trim();
  }
  if (typeof error.hint === "string" && error.hint.trim()) {
    return error.hint.trim();
  }

  try {
    return JSON.stringify(error);
  } catch (_error) {
    return "Unknown error";
  }
};

const isStatementTimeoutError = (error) => {
  if (!error) return false;

  const code = String(error?.code || "").trim();
  if (code === "57014") return true;

  const message = extractErrorMessage(error).toLowerCase();
  return message.includes("statement timeout") || message.includes("canceling statement");
};

const MODEL_REVIEW_COLUMN_NAMES = [
  "model_output_json",
  "model_output_review",
  "model_output_review_updated_at",
  "model_output_review_updated_by",
];

const normalizeCorrectionOptionKey = (label) =>
  String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const isMissingModelReviewColumnError = (error) => {
  const message = extractErrorMessage(error).toLowerCase();
  return MODEL_REVIEW_COLUMN_NAMES.some((columnName) =>
    message.includes(columnName.toLowerCase())
  );
};

const buildDashboardConsultationsSelect = ({
  usersSelect,
  includeModelReviewFields = true,
}) => `
          id,
          user_id,
          skin_type,
          consultation_type,
          hair_user_consultation_id,
          body_user_consultation_id,
          relationship_status,
          skin_sensitivity,
          additional_sensitivity,
          budget,
          detailed_diagnosis,
          image_validation_status,
          image_validation_decision,
          dermatologist_analysis,
          ${
            includeModelReviewFields
              ? `
          model_output_json,
          model_output_review,
          model_output_review_updated_at,
          model_output_review_updated_by,
          `
              : ""
          }
          products_offered,
          consultation_completed,
          consultation_status,
          consultation_outcome,
          wondura_done_at,
          completed_at,
          assigned_pharmacist,
          assignment_changed,
          reassigned_at,
          previous_pharmacist,
          reassigned_by,
          created_at,
          updated_at,
          consultation_images (
            id,
            consultation_id,
            user_id,
            image_path,
            image_type,
            upload_timestamp
          ),
          ${usersSelect}
        `;

const capDashboardInterfaceTotal = (count) => {
  if (typeof count !== "number" || Number.isNaN(count)) {
    return null;
  }

  return Math.min(count, DASHBOARD_INTERFACE_RESULT_LIMIT);
};

const buildEmptyDashboardStats = () => ({
  totalConsultations: 0,
  pendingCount: 0,
  wonduraDone: 0,
  completedCount: 0,
  paidCount: 0,
  hotLeadCount: 0,
  coldLeadCount: 0,
  conversionRate: 0,
  avgProcessingTime: 0,
});

export class AdminService {
  // ============================================
  // AUTHENTICATION METHODS
  // ============================================

  // Admin authentication using Supabase Auth (DEPRECATED - use sendMagicLink + verifyAdminProfile)
  // Kept for backwards compatibility during transition
  static async authenticate(email, password) {
    try {
      console.log("🔐 Attempting login for email:", email);

      // Step 1: Authenticate with Supabase Auth
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email: email,
          password: password,
        });

      if (authError) {
        console.error("❌ Supabase auth error:", authError);
        return { success: false, error: "Invalid credentials" };
      }

      if (!authData.user) {
        console.warn("⚠️ No user returned from auth");
        return { success: false, error: "Authentication failed" };
      }

      console.log("✅ Supabase auth successful, fetching admin profile...");

      // Step 2: Fetch admin profile from admin_users table
      // Using maybeSingle() first to check for multiple entries
      const { data: adminUsers, error: profileError } = await supabase
        .from("admin_users")
        .select("id, username, role, is_active")
        .eq("auth_user_id", authData.user.id);

      if (profileError) {
        console.error("❌ Admin profile query error:", profileError);
        return { success: false, error: "Could not verify your account. Please try again.", retryable: true };
      }

      // Check if no admin user found
      if (!adminUsers || adminUsers.length === 0) {
        console.error(
          "❌ No admin profile found for auth_user_id:",
          authData.user.id
        );
        await supabase.auth.signOut();
        return {
          success: false,
          error: "Not authorized as admin - no profile found",
        };
      }

      // Check if multiple admin users found (shouldn't happen)
      if (adminUsers.length > 1) {
        console.error(
          "❌ Multiple admin profiles found for same auth_user_id:",
          adminUsers
        );
        await supabase.auth.signOut();
        return {
          success: false,
          error: "Multiple admin profiles found - please contact administrator",
        };
      }

      const adminUser = adminUsers[0];

      if (!adminUser) {
        console.error("❌ Admin profile not found");
        await supabase.auth.signOut();
        return { success: false, error: "Not authorized as admin" };
      }

      // Step 3: Check if admin is active
      if (!adminUser.is_active) {
        console.warn("⚠️ Admin account is deactivated:", adminUser.username);
        await supabase.auth.signOut();
        return { success: false, error: "Account is deactivated" };
      }

      // Step 4: Store admin session info in localStorage for quick access
      localStorage.setItem("admin_authenticated", "true");
      localStorage.setItem("admin_user", JSON.stringify(adminUser));

      console.log(
        "✅ Admin authenticated:",
        adminUser.username,
        "Role:",
        adminUser.role
      );
      return { success: true, user: adminUser };
    } catch (error) {
      console.error("❌ Admin authentication error:", error);
      return { success: false, error: "Authentication failed" };
    }
  }

  // NEW: Send magic link to user's email
  static async sendMagicLink(email) {
    try {
      console.log("📧 Sending magic link to:", email);

      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/admin/login`,
          shouldCreateUser: false, // Don't auto-create users
        },
      });

      if (error) {
        console.error("❌ Magic link error:", error);
        return { success: false, error: error.message };
      }

      console.log("✅ Magic link sent successfully");
      return { success: true };
    } catch (error) {
      console.error("❌ Send magic link error:", error);
      return { success: false, error: "Failed to send magic link" };
    }
  }

  // NEW: Verify admin profile after magic link authentication
  static async verifyAdminProfile() {
    try {
      // console.log("🔍 Verifying admin profile...");

      // Get current authenticated user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        console.error("❌ No authenticated user:", userError);
        return { success: false, error: "Not authenticated" };
      }

      console.log("✅ Auth user found:", user.id);

      // Fetch admin profile
      const { data: adminUsers, error: profileError } = await supabase
        .from("admin_users")
        .select("id, username, role, is_active, mfa_enabled, email_verified")
        .eq("auth_user_id", user.id);

      if (profileError) {
        console.error("❌ Admin profile query error:", profileError);
        return { success: false, error: "Could not verify your account. Please try again.", retryable: true };
      }

      if (!adminUsers || adminUsers.length === 0) {
        console.error("❌ No admin profile found");
        await supabase.auth.signOut();
        return {
          success: false,
          error: "Not authorized as admin - no profile found",
        };
      }

      if (adminUsers.length > 1) {
        console.error("❌ Multiple admin profiles found");
        await supabase.auth.signOut();
        return {
          success: false,
          error: "Multiple admin profiles found - please contact administrator",
        };
      }

      const adminUser = adminUsers[0];

      // Check if admin is active
      if (!adminUser.is_active) {
        console.warn("⚠️ Admin account is deactivated:", adminUser.username);
        await supabase.auth.signOut();
        return { success: false, error: "Account is deactivated" };
      }

      // Store admin session info
      localStorage.setItem("admin_authenticated", "true");
      localStorage.setItem("admin_user", JSON.stringify(adminUser));

      console.log(
        "✅ Admin profile verified:",
        adminUser.username,
        "Role:",
        adminUser.role
      );
      console.log(
        "   MFA enabled:",
        adminUser.mfa_enabled,
        "Email verified:",
        adminUser.email_verified
      );

      return {
        success: true,
        user: adminUser,
        requiresMFA: !adminUser.mfa_enabled,
        requiresVerification: !adminUser.email_verified,
      };
    } catch (error) {
      console.error("❌ Verify admin profile error:", error);
      return { success: false, error: "Profile verification failed" };
    }
  }

  // NEW: Robust synchronization between Supabase Auth MFA and and admin_users table
  static async syncMFAStatus() {
    try {
      console.log("🔄 Syncing MFA status from Supabase Auth...");

      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) throw listError;

      const verifiedFactors = [
        ...(factors?.totp || []),
        ...(factors?.all || [])
      ].filter(f => f.verified === true || f.status === 'verified');

      const hasVerifiedMFA = verifiedFactors.length > 0;

      // Get current stored user
      const currentUser = this.getCurrentUser();
      if (!currentUser) return { success: false, error: "No user logged in" };

      // If Supabase says they have MFA but our DB says they don't, REPAIR IT
      if (hasVerifiedMFA && !currentUser.mfa_enabled) {
        console.log("🛠️ Auto-repairing MFA status in database...");
        await this.markMFAEnabled();
        return { success: true, repaired: true };
      }

      return { success: true, repaired: false, hasVerifiedMFA };
    } catch (error) {
      console.error("❌ Sync MFA status failed:", error);
      return { success: false, error: error.message };
    }
  }

  // NEW: Mark MFA as enabled after successful enrollment
  static async markMFAEnabled() {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) {
        return { success: false, error: "No current user" };
      }

      console.log("🔐 Marking MFA enabled for:", currentUser.username);

      const { error } = await supabase
        .from("admin_users")
        .update({
          mfa_enabled: true,
          email_verified: true,
          is_active: true, // Activate user after MFA setup
        })
        .eq("id", currentUser.id);

      if (error) {
        console.error("❌ Mark MFA enabled error:", error);
        return { success: false, error: error.message };
      }

      // Update localStorage
      const updatedUser = {
        ...currentUser,
        mfa_enabled: true,
        email_verified: true,
        is_active: true,
      };
      localStorage.setItem("admin_user", JSON.stringify(updatedUser));

      console.log("✅ MFA enabled and user activated");
      return { success: true, user: updatedUser };
    } catch (error) {
      console.error("❌ Mark MFA enabled error:", error);
      return { success: false, error: "Failed to mark MFA enabled" };
    }
  }

  // Check if admin is authenticated (verifies Supabase session + localStorage sync)
  // This properly handles sessions that persist for 1 week
  static isAuthenticated() {
    // First check localStorage flag (quick check)
    const localAuth = localStorage.getItem('admin_authenticated') === 'true';

    // For synchronous check (used in render), we rely on the auth state listener
    // to keep localStorage in sync with the actual Supabase session
    // The onAuthStateChange listener in supabase.js handles:
    // - SIGNED_IN: sets localStorage
    // - SIGNED_OUT: clears localStorage
    // - TOKEN_REFRESH_ERROR: clears localStorage

    return localAuth;
  }

  // ============================================
  // MULTI-SESSION HANDLING - THE GOLDEN FIX
  // ============================================

  // Static promise to prevent multiple simultaneous refresh attempts
  // This is the KEY to multi-session stability
  static _refreshPromise = null;

  // Session isolation - different storage keys for different contexts
  static _getSessionStorageKey() {
    // Get current user to determine session context
    const user = this.getCurrentUser();
    if (user?.role) {
      return `admin_session_${user.role}_${user.id}`;
    }
    return 'admin_session_default';
  }

  // THE GOLDEN METHOD: Deduplicated session verification
  static async verifySession(options = {}) {
    const { logoutOnFailure = true } = options;

    // If a refresh is already in progress, wait for it instead of starting a new one
    // This prevents race conditions and supports multi-session scenarios
    if (this._refreshPromise) {
      console.log('⏳ Session refresh already in progress, waiting...');
      return this._refreshPromise;
    }

    // Create the refresh promise
    this._refreshPromise = this._doVerifySession({ logoutOnFailure });

    try {
      const result = await this._refreshPromise;
      return result;
    } finally {
      // Clear the promise so future calls can refresh
      this._refreshPromise = null;
    }
  }

  // Internal method that does the actual verification
  static async _doVerifySession(options = {}) {
    const { logoutOnFailure = true } = options;

    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('❌ Session verification error:', error);
        if (logoutOnFailure && isTerminalSessionError(error)) clearCachedAdminIdentity();
        return false;
      }

      if (!session) {
        console.warn('⚠️ No active Supabase session');
        if (logoutOnFailure) clearCachedAdminIdentity();
        return false;
      }

      // Check if token is expired or about to expire (with 5-minute buffer)
      if (session.expires_at) {
        const expiresAt = new Date(session.expires_at * 1000);
        const now = new Date();
        const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

        if (expiresAt < new Date(now.getTime() + bufferTime)) {
          console.log('🔄 Token expiring soon, refreshing...');
          const { error: refreshError } = await supabase.auth.refreshSession();

          if (refreshError) {
            console.error('❌ Token refresh failed:', refreshError);
            if (logoutOnFailure && isTerminalSessionError(refreshError)) clearCachedAdminIdentity();
            return false;
          }

          console.log('✅ Token refreshed successfully');
        }
      }

      // Verify localStorage matches session
      if (localStorage.getItem('admin_authenticated') !== 'true') {
        console.log('🔄 Syncing localStorage with active session');
        localStorage.setItem('admin_authenticated', 'true');
      }

      return true;
    } catch (error) {
      console.error('❌ Session verification error:', error);
      return false;
    }
  }

  static _isLikelySessionError(error) {
    const message = typeof error === "string"
      ? error.toLowerCase()
      : String(error?.message || "").toLowerCase();

    return (
      message.includes("jwt") ||
      message.includes("token") ||
      message.includes("session") ||
      message.includes("auth") ||
      message.includes("expired") ||
      message.includes("refresh") ||
      message.includes("row-level security") ||
      message.includes("permission denied")
    );
  }

  // Get current admin user data from localStorage
  static getCurrentUser() {
    try {
      const userData = localStorage.getItem("admin_user");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("❌ Error getting current user:", error);
      return null;
    }
  }

  // Get current admin user role
  static getCurrentUserRole() {
    const user = this.getCurrentUser();
    return user?.role || null;
  }

  // Check if current user is super admin
  static isSuperAdmin() {
    return this.getCurrentUserRole() === "super_admin";
  }

  // Check if current user is coordinator
  static isCoordinator() {
    return this.getCurrentUserRole() === "coordinator";
  }

  // Check if current user can manage users (super_admin only)
  static canManageUsers() {
    return this.getCurrentUserRole() === "super_admin";
  }

  // Check if current user can set consultation outcomes (super_admin or pharmacist, NOT coordinator)
  static canSetOutcome() {
    const role = this.getCurrentUserRole();
    return role === "super_admin" || role === "pharmacist";
  }

  // Admin logout - clears both Supabase session and localStorage
  static async logout() {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error("❌ Supabase signout error:", error);
    }
    localStorage.removeItem("admin_authenticated");
    localStorage.removeItem("admin_user");
    localStorage.removeItem("admin_mfa_challenge_completed");
    localStorage.removeItem("admin_mfa_verified_session_id");
    localStorage.removeItem("admin_mfa_challenge_required");
    localStorage.removeItem("admin_mfa_factor_id");

    // 🧹 Tab-specific cleanup
    sessionStorage.removeItem("admin_mfa_verified_session_id");
    sessionStorage.removeItem("admin_mfa_challenge_required");
    sessionStorage.removeItem("admin_mfa_factor_id");

    // 🔒 Clear session manager
    SessionManager.clearSession();
    console.log("👋 Admin logged out");
  }

  // ============================================
  // DASHBOARD DATA METHODS
  // ============================================

  /* eslint-disable no-unreachable */
  // Get dashboard data - main query with all the info you requested
  // Supports server-side filtering for searchTerm, dateRange, and status
  static async getDashboardData(filters = {}) {
    return this._getDashboardDataV2(filters);

    try {
      // console.log('📊 Fetching dashboard data with filters:', filters);

      // Get current user role and username for filtering
      const currentUser = this.getCurrentUser();
      const userRole = currentUser?.role;
      const username = currentUser?.username;

      // Extract filter parameters
      const {
        searchTerm = '',
        dateRange = null, // { from: ISO date, to: ISO date }
        filterStatus = 'all'
      } = filters;

      // console.log('👤 Current user:', username, 'Role:', userRole);
      // console.log('🔍 Search term:', searchTerm);
      // console.log('📅 Date range:', dateRange);
      // console.log('🏷️ Status filter:', filterStatus);

      // Build base query with filters
      let query = supabase
        .from('users')
        .select(
          `
          id,
          name,
          phone,
          session_id,
          is_anonymous,
          created_at,
          updated_at,
            user_consultations (
              id,
              skin_type,
              consultation_type,
              hair_user_consultation_id,
              body_user_consultation_id,
              relationship_status,
              skin_sensitivity,
              additional_sensitivity,
              budget,
              detailed_diagnosis,
              image_validation_status,
              image_validation_decision,
              dermatologist_analysis,
              products_offered,
              consultation_completed,
              consultation_status,
              consultation_outcome,
              wondura_done_at,
              completed_at,
              assigned_pharmacist,
              assignment_changed,
              reassigned_at,
              previous_pharmacist,
              reassigned_by,
              created_at,
              updated_at
            ),
          consultation_images (
            id,
            image_path,
            image_type,
            upload_timestamp
          )
        `
        );

      // Apply date range filter (on users.created_at)
      if (dateRange?.from) {
        query = query.gte('created_at', dateRange.from);
        // console.log('🔍 Filtering from date:', dateRange.from);
      }
      if (dateRange?.to) {
        query = query.lte('created_at', dateRange.to);
        // console.log('🔍 Filtering to date:', dateRange.to);
      }

      // Apply search filter (phone or user_id)
      // Note: UUID fields don't support ilike, so we use exact match for id
      // Phone search handles special characters like + (ilike supports this)
      if (searchTerm && searchTerm.trim()) {
        const search = searchTerm.trim();

        // Check if search term is a UUID
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(search);

        if (isUUID) {
          // For UUID, use exact match on id field
          query = query.eq('id', search);
          // console.log('🔍 Applied UUID search filter:', search);
        } else {
          // For text search, only search by phone to keep queries fast
          query = query.ilike("phone", `%${search}%`);
          // console.log('🔍 Applied text/phone search filter:', search);
        }
      }

      // 🔥 OPTIMIZATION: For pharmacists, only fetch their assigned consultations
      // This reduces the dataset and prevents RLS timeout
      if (userRole === 'pharmacist' && username) {
        query = query.eq('user_consultations.assigned_pharmacist', username);
        // console.log('🔍 Filtering for pharmacist:', username);
      }

      // Apply status filter via user_consultations
      if (filterStatus !== 'all') {
        query = query.eq('user_consultations.consultation_status', filterStatus);
        // console.log('🔍 Applied status filter:', filterStatus);
      }

      // Order by created_at descending and add safety limit
      query = query.order('created_at', { ascending: false });

      // 🔥 OPTIMIZATION: Reduce limit for pharmacists to prevent timeout
      const limit =
        userRole === "pharmacist"
          ? Math.min(100, DASHBOARD_INTERFACE_RESULT_LIMIT)
          : DASHBOARD_INTERFACE_RESULT_LIMIT;
      query = query.limit(limit);

      const { data, error } = await query;

      if (error) {
        console.error("❌ Dashboard data error:", error);
        return { data: null, error };
      }

      // Debug: Log raw data to see what we're getting
      // console.log(
      //   "🔍 Raw data from Supabase:",
      //   JSON.stringify(data?.[0], null, 2)
      // );
      // */

      // Fetch ALL channel mappings at once (more efficient than filtering by hundreds of IDs)
      // This avoids URL length limits that cause CORS errors
      const { data: allChannelMappings } = await supabase
        .from("consultation_channel_mappings")
        .select("consultation_id, user_id, contact_id");

      // Create maps for quick lookup by both consultation_id and user_id
      const contactIdByConsultationMap = {};
      const contactIdByUserMap = {};

      if (allChannelMappings) {
        allChannelMappings.forEach((mapping) => {
          if (mapping.consultation_id) {
            contactIdByConsultationMap[mapping.consultation_id] =
              mapping.contact_id;
          }
          if (mapping.user_id) {
            contactIdByUserMap[mapping.user_id] = mapping.contact_id;
          }
        });
      }

      // Fetch hair and body consultations separately (manual join) to avoid relationship errors
      // Use user_consultation_id as the link since user_id might be null in hair_user_consultations/body_user_consultations 
      const hairConsultationIds = [];
      const bodyConsultationIds = [];
      data.forEach(user => {
        const consults = Array.isArray(user.user_consultations)
          ? user.user_consultations
          : [user.user_consultations];
        consults.forEach(c => {
          if (c?.id) {
            if (c.hair_user_consultation_id) hairConsultationIds.push(c.id);
            if (c.body_user_consultation_id) bodyConsultationIds.push(c.id);
          }
        });
      });

      const hairConsultationMap = {};
      const bodyConsultationMap = {};

      // Helper function to fetch in chunks to avoid URL length limit (Bad Request 400)
      const fetchInChunks = async (ids, table, mapToUpdate) => {
        if (ids.length === 0) return;

        const chunkSize = 100;
        for (let i = 0; i < ids.length; i += chunkSize) {
          const chunk = ids.slice(i, i + chunkSize);

          const { data: chunkData, error: chunkError } = await supabase
            .from(table)
            .select('*')
            .in('user_consultation_id', chunk);

          if (chunkError) {
            console.error(`❌ Error fetching ${table} consultations:`, chunkError);
          } else if (chunkData) {
            chunkData.forEach(item => {
              mapToUpdate[item.user_consultation_id] = item;
            });
          }
        }
      };

      await Promise.all([
        fetchInChunks(hairConsultationIds, 'hair_user_consultations', hairConsultationMap),
        fetchInChunks(bodyConsultationIds, 'body_user_consultations', bodyConsultationMap)
      ]);

      // Transform data for easier display
      const transformedData = data.map((user) => {
        // Handle both array and single object format for consultations
        const consultation = Array.isArray(user.user_consultations)
          ? user.user_consultations?.[0]
          : user.user_consultations;
        const image = user.consultation_images?.[0];

        // Process consultation types into array
        let consultationTypes = [];
        if (consultation?.consultation_type) {
          consultationTypes = consultation.consultation_type.split(',').map(t => t.trim()).filter(Boolean);
        }
        if (consultationTypes.length === 0) {
          // Default backward compatibility
          consultationTypes = ['Skin'];
        }

        // Get contact_id from the maps - try consultation_id first, then user_id
        const contactId =
          (consultation?.id && contactIdByConsultationMap[consultation.id]) ||
          contactIdByUserMap[user.id] ||
          null;

        // if (consultation) {
        //   console.log("🔍 Consultation data for user:", user.name, {
        //     skin_type: consultation.skin_type,
        //     skin_type_length: consultation.skin_type?.length,
        //     relationship_status: consultation.relationship_status,
        //     relationship_status_length:
        //       consultation.relationship_status?.length,
        //     relationship_status_type: typeof consultation.relationship_status,
        //     skin_sensitivity: consultation.skin_sensitivity,
        //     skin_sensitivity_type: typeof consultation.skin_sensitivity,
        //     skin_sensitivity_length: consultation.skin_sensitivity?.length,
        //     skin_sensitivity_is_array: Array.isArray(
        //       consultation.skin_sensitivity
        //     ),
        //     additional_sensitivity: consultation.additional_sensitivity,
        //     additional_sensitivity_length:
        //       consultation.additional_sensitivity?.length,
        //   });
        // }

        // Combine skin_sensitivity and additional_sensitivity with better handling
        let sensitivityDetails = "None";

        // Handle skin_sensitivity array
        let sensitivities = [];
        if (consultation?.skin_sensitivity) {
          if (Array.isArray(consultation.skin_sensitivity)) {
            // Filter out empty strings and null values
            sensitivities = consultation.skin_sensitivity.filter(
              (item) =>
                item && typeof item === "string" && item.trim().length > 0
            );
          } else if (
            typeof consultation.skin_sensitivity === "string" &&
            consultation.skin_sensitivity.trim()
          ) {
            sensitivities = [consultation.skin_sensitivity.trim()];
          }
        }

        // Handle additional_sensitivity
        if (
          consultation?.additional_sensitivity &&
          typeof consultation.additional_sensitivity === "string" &&
          consultation.additional_sensitivity.trim()
        ) {
          sensitivities.push(consultation.additional_sensitivity.trim());
        }

        // Set final sensitivity details
        if (sensitivities.length > 0) {
          sensitivityDetails = sensitivities.join(", ");
        }

        /* console.log("🔍 Final sensitivity processing:", {
          raw_sensitivities: sensitivities,
          final_result: sensitivityDetails,
        }); */

        // Calculate processing time if available (Skin)
        let processingTime = null;
        if (consultation?.wondura_done_at && consultation?.completed_at) {
          const wonduraTime = new Date(consultation.wondura_done_at);
          const completedTime = new Date(consultation.completed_at);
          const diffMs = completedTime - wonduraTime;
          const diffMins = Math.floor(diffMs / 60000);
          processingTime = diffMins > 0 ? `${diffMins} min` : "< 1 min";
        }

        // Handle hair consultation record if linked
        // Match using the specific consultation ID we are processing
        const hairConsultation = consultation?.id ? hairConsultationMap[consultation.id] : null;
        const bodyConsultation = consultation?.id ? bodyConsultationMap[consultation.id] : null;

        // Calculate hair processing time if available
        let hairProcessingTime = null;
        if (hairConsultation?.wondura_done_at && hairConsultation?.completed_at) {
          const hairWonduraTime = new Date(hairConsultation.wondura_done_at);
          const hairCompletedTime = new Date(hairConsultation.completed_at);
          const diffMs = hairCompletedTime - hairWonduraTime;
          const diffMins = Math.floor(diffMs / 60000);
          hairProcessingTime = diffMins > 0 ? `${diffMins} min` : "< 1 min";
        }

        // Calculate body processing time if available
        let bodyProcessingTime = null;
        if (bodyConsultation?.wondura_done_at && bodyConsultation?.completed_at) {
          const bodyWonduraTime = new Date(bodyConsultation.wondura_done_at);
          const bodyCompletedTime = new Date(bodyConsultation.completed_at);
          const diffMs = bodyCompletedTime - bodyWonduraTime;
          const diffMins = Math.floor(diffMs / 60000);
          bodyProcessingTime = diffMins > 0 ? `${diffMins} min` : "< 1 min";
        }

        return {
          user_id: user.id,
          consultation_id: consultation?.id || null,
          consultation_types: consultationTypes,
          name: user.name || "Anonymous",
          phone: user.phone || "N/A",
          session_id: user.session_id || null,
          is_anonymous: user.is_anonymous || false,
          contact_id: contactId || null,
          skin_type:
            consultation?.skin_type &&
              typeof consultation.skin_type === "string" &&
              consultation.skin_type.trim().length > 0
              ? consultation.skin_type.trim()
              : "Not specified",
          relationship_status:
            consultation?.relationship_status || "Not specified",
          budget: consultation?.budget || "Not specified",
          sensitivity_details: sensitivityDetails,
          photo_link: image ? image.image_path : null,
          image_validation_status: normalizeImageValidationStatus(
            consultation
          ),
          image_validation_decision:
            consultation?.image_validation_decision || null,
          image_analysis: deriveImageAnalysisDisplay(consultation),
          image_analysis_details_available: Boolean(
            getImageAnalysisDetailsText(consultation)
          ),
          derma_output: consultation?.dermatologist_analysis || "Pending",
          routine_output: parseProductsOffered(consultation?.products_offered)
            ? "Generated"
            : "Pending",
          routine_products: parseProductsOffered(consultation?.products_offered) || null,
          consultation_status: consultation?.consultation_status || "pending",
          consultation_outcome: consultation?.consultation_outcome || null,
          assigned_pharmacist: consultation?.assigned_pharmacist || null,
          assignment_changed: consultation?.assignment_changed || false,
          reassigned_at: consultation?.reassigned_at || null,
          previous_pharmacist: consultation?.previous_pharmacist || null,
          reassigned_by: consultation?.reassigned_by || null,
          wondura_done_at: consultation?.wondura_done_at || null,
          completed_at: consultation?.completed_at || null,
          processing_time: processingTime,

          // Hair consultation fields
          has_hair_consultation: !!hairConsultation,
          hair_consultation_status: hairConsultation?.consultation_status || null,
          hair_wondura_done_at: hairConsultation?.wondura_done_at || null,
          hair_completed_at: hairConsultation?.completed_at || null,
          hair_processing_time: hairProcessingTime,
          hair_analysis: hairConsultation?.detailed_diagnosis || "Pending",
          hair_derma_output: hairConsultation?.dermatologist_analysis || "Pending",
          hair_routine_products: parseProductsOffered(hairConsultation?.product_offer) || null,
          hair_budget: hairConsultation?.budget || null,
          hair_routine_output: parseProductsOffered(hairConsultation?.product_offer) ? "Generated" : "Pending",
          hair_type: hairConsultation?.Hair_Type || null,
          hair_texture: hairConsultation?.Hair_Texture || null,
          hair_problem: hairConsultation?.Hair_Problem || null,
          hair_any_issues: hairConsultation?.any_issues || null,

          // Body consultation fields
          has_body_consultation: !!bodyConsultation,
          body_consultation_status: bodyConsultation?.consultation_status || null,
          body_wondura_done_at: bodyConsultation?.wondura_done_at || null,
          body_completed_at: bodyConsultation?.completed_at || null,
          body_processing_time: bodyProcessingTime,
          body_analysis: bodyConsultation?.detailed_diagnosis || "Pending",
          body_derma_output: bodyConsultation?.dermatologist_analysis || "Pending",
          body_routine_products: parseProductsOffered(bodyConsultation?.product_offer) || null,
          body_budget: bodyConsultation?.budget || null,
          body_routine_output: parseProductsOffered(bodyConsultation?.product_offer) ? "Generated" : "Pending",
          body_concerns: bodyConsultation?.body_concerns || null,
          body_relationship_status: bodyConsultation?.relationship_status || null,
          body_skin_sensitivity: bodyConsultation?.skin_sensitivity || null,
          body_additional_sensitivity: bodyConsultation?.additional_sensitivity || null,

          updated_at: new Date(
            Math.max(
              new Date(user.updated_at || 0),
              new Date(consultation?.updated_at || 0)
            )
          ).toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
      });

      // For pharmacists, filter to show only consultations assigned to them
      const filteredData =
        userRole === "pharmacist"
          ? transformedData.filter(
            (item) => item.assigned_pharmacist === username
          )
          : transformedData;

      console.log(
        `✅ Dashboard data fetched: ${filteredData.length} records (Role: ${userRole})`
      );
      return { data: filteredData, error: null };
    } catch (error) {
      console.error("❌ Error fetching dashboard data:", error);
      return { data: null, error };
    }
  }
  /* eslint-enable no-unreachable */

  static async getDashboardStats(filters = {}) {
    try {
      const currentUser = this.getCurrentUser();
      const userRole = currentUser?.role;
      const username = currentUser?.username;

      const {
        searchTerm = "",
        dateRange = null,
        filterStatus = "all",
      } = filters;

      const searchContext = getDashboardSearchContext(searchTerm);
      const searchResolution = await this._resolveDashboardSearchUserIds(
        searchContext,
        {
          dateRange,
          filterStatus,
          userRole,
          username,
        }
      );
      const searchFilterState = getDashboardSearchFilterState({
        searchContext,
        searchResolution,
      });

      if (searchFilterState.shouldAbort) {
        throw buildDashboardSearchUnavailableError(searchResolution);
      }

      if (searchFilterState.isResolvedEmpty) {
        return { stats: buildEmptyDashboardStats(), error: null };
      }

      const stats = await this._getDashboardStats({
        searchTerm,
        dateRange,
        filterStatus,
        userRole,
        username,
        searchContext,
        searchResolution,
        countMode: "exact",
      });

      return { stats, error: null };
    } catch (error) {
      console.error("❌ Error fetching dashboard stats:", error);
      return {
        stats: null,
        error: new Error(extractErrorMessage(error)),
      };
    }
  }

  static async _getDashboardDataV2(filters = {}) {
    try {
      const currentUser = this.getCurrentUser();
      const userRole = currentUser?.role;
      const username = currentUser?.username;
      const isPharmacist = userRole === "pharmacist";

      const {
        searchTerm = "",
        dateRange = null,
        filterStatus = "all",
        page = 1,
        pageSize = 10,
        fetchAll = false,
        includeStats = true,
        includeTotal = true,
      } = filters;

      const pageNumber = Math.max(Number(page) || 1, 1);
      const safePageSize = Math.max(Number(pageSize) || 10, 1);
      const searchContext = getDashboardSearchContext(searchTerm);
      const searchResolution = await this._resolveDashboardSearchUserIds(
        searchContext,
        {
          dateRange,
          filterStatus,
          userRole,
          username,
        }
      );
      const searchFilterState = getDashboardSearchFilterState({
        searchContext,
        searchResolution,
      });

      if (searchFilterState.shouldAbort) {
        throw buildDashboardSearchUnavailableError(searchResolution);
      }

      if (searchFilterState.isResolvedEmpty) {
        return {
          data: [],
          total: includeTotal || fetchAll ? 0 : null,
          page: pageNumber,
          pageSize: safePageSize,
          stats: includeStats ? buildEmptyDashboardStats() : null,
          error: null,
        };
      }

      const usersSelect = getDashboardUsersSelect({
        searchContext,
        searchResolution,
      });
      const countMode = "exact";

      const buildDashboardQuery = (includeModelReviewFields = true) => {
        let query = supabase
          .from("user_consultations")
          .select(
            buildDashboardConsultationsSelect({
              usersSelect,
              includeModelReviewFields,
            })
          );

        query = this._applyDashboardFilters(query, {
          dateRange,
          filterStatus,
          userRole,
          username,
          searchContext,
          searchResolution,
        })
          .order("created_at", { ascending: false })
          .order("id", { ascending: false });

        if (!fetchAll) {
          const from = (pageNumber - 1) * safePageSize;
          const extraRowAllowance = isPharmacist ? 1 : 0;
          const to = Math.min(
            from + safePageSize + extraRowAllowance - 1,
            DASHBOARD_INTERFACE_RESULT_LIMIT - 1
          );
          query = query.range(from, to);
        }

        return query;
      };

      let { data, error } = await buildDashboardQuery(true);

      if (error && isMissingModelReviewColumnError(error)) {
        console.warn(
          "⚠️ Dashboard model review columns are not available yet, retrying without them."
        );
        ({ data, error } = await buildDashboardQuery(false));
      }

      if (error) {
        console.error("❌ Dashboard data error:", error);
        return {
          data: null,
          error: new Error(extractErrorMessage(error)),
          total: 0,
          stats: null,
        };
      }

      const rawConsultations = data || [];
      const hasMoreRows =
        !fetchAll && isPharmacist && rawConsultations.length > safePageSize;
      const consultations = hasMoreRows
        ? rawConsultations.slice(0, safePageSize)
        : rawConsultations;
      const consultationIds = [
        ...new Set(consultations.map((item) => item.id).filter(Boolean)),
      ];
      const userIds = [
        ...new Set(consultations.map((item) => item.user_id).filter(Boolean)),
      ];

      const [contactMaps, hairConsultationMap, bodyConsultationMap, fallbackImageMap] =
        await Promise.all([
          this._fetchDashboardContactMaps({ consultationIds, userIds }),
          this._fetchDashboardSubrecords(
            consultations
              .filter((item) => item.hair_user_consultation_id)
              .map((item) => item.id),
            "hair_user_consultations"
          ),
          this._fetchDashboardSubrecords(
            consultations
              .filter((item) => item.body_user_consultation_id)
              .map((item) => item.id),
            "body_user_consultations"
          ),
          this._fetchDashboardFallbackImages(userIds),
        ]);

      const transformedData = consultations.map((consultation) => {
        const user = Array.isArray(consultation.users)
          ? consultation.users[0]
          : consultation.users;
        const hairConsultation = consultation.id
          ? hairConsultationMap[consultation.id]
          : null;
        const bodyConsultation = consultation.id
          ? bodyConsultationMap[consultation.id]
          : null;
        const primaryImage =
          pickLatestByTimestamp(
            ensureArray(consultation.consultation_images),
            "upload_timestamp"
          ) || fallbackImageMap[consultation.user_id] || null;
        const skinRoutineProducts = parseProductsOffered(
          consultation?.products_offered
        );
        const hairRoutineProducts = parseProductsOffered(
          hairConsultation?.product_offer
        );
        const bodyRoutineProducts = parseProductsOffered(
          bodyConsultation?.product_offer
        );
        const latestUpdatedAt = Math.max(
          new Date(user?.updated_at || 0).getTime(),
          new Date(consultation?.updated_at || 0).getTime(),
          new Date(hairConsultation?.updated_at || 0).getTime(),
          new Date(bodyConsultation?.updated_at || 0).getTime()
        );

        return {
          user_id: consultation.user_id,
          consultation_id: consultation.id,
          consultation_types: normalizeConsultationTypes(
            consultation.consultation_type
          ),
          name: user?.name || "Anonymous",
          phone: user?.phone || "N/A",
          session_id: user?.session_id || null,
          is_anonymous: user?.is_anonymous || false,
          contact_id:
            contactMaps.contactIdByConsultationMap[consultation.id] ||
            contactMaps.contactIdByUserMap[consultation.user_id] ||
            null,
          skin_type:
            typeof consultation?.skin_type === "string" &&
            consultation.skin_type.trim()
              ? consultation.skin_type.trim()
              : "Not specified",
          relationship_status:
            consultation?.relationship_status || "Not specified",
          budget: consultation?.budget || "Not specified",
          sensitivity_details: buildSensitivityDetails(
            consultation?.skin_sensitivity,
            consultation?.additional_sensitivity
          ),
          photo_link: primaryImage?.image_path || null,
          image_validation_status: normalizeImageValidationStatus(
            consultation
          ),
          image_validation_decision:
            consultation?.image_validation_decision || null,
          image_analysis: deriveImageAnalysisDisplay(consultation),
          image_analysis_details_available: Boolean(
            getImageAnalysisDetailsText(consultation)
          ),
          derma_output: consultation?.dermatologist_analysis || "Pending",
          model_output_json: consultation?.model_output_json || null,
          model_output_review: consultation?.model_output_review || null,
          model_output_review_updated_at:
            consultation?.model_output_review_updated_at || null,
          model_output_review_updated_by:
            consultation?.model_output_review_updated_by || null,
          routine_output: skinRoutineProducts ? "Generated" : "Pending",
          routine_products: skinRoutineProducts || null,
          consultation_status: consultation?.consultation_status || "pending",
          consultation_outcome: consultation?.consultation_outcome || null,
          assigned_pharmacist: consultation?.assigned_pharmacist || null,
          assignment_changed: consultation?.assignment_changed || false,
          reassigned_at: consultation?.reassigned_at || null,
          previous_pharmacist: consultation?.previous_pharmacist || null,
          reassigned_by: consultation?.reassigned_by || null,
          wondura_done_at: consultation?.wondura_done_at || null,
          completed_at: consultation?.completed_at || null,
          processing_time: calculateProcessingTime(
            consultation?.wondura_done_at,
            consultation?.completed_at
          ),
          has_hair_consultation: !!hairConsultation,
          hair_consultation_status:
            hairConsultation?.consultation_status || null,
          hair_wondura_done_at: hairConsultation?.wondura_done_at || null,
          hair_completed_at: hairConsultation?.completed_at || null,
          hair_processing_time: calculateProcessingTime(
            hairConsultation?.wondura_done_at,
            hairConsultation?.completed_at
          ),
          hair_analysis: hairConsultation?.detailed_diagnosis || "Pending",
          hair_derma_output:
            hairConsultation?.dermatologist_analysis || "Pending",
          hair_routine_products: hairRoutineProducts || null,
          hair_budget: hairConsultation?.budget || null,
          hair_routine_output: hairRoutineProducts ? "Generated" : "Pending",
          hair_type: hairConsultation?.Hair_Type || null,
          hair_texture: hairConsultation?.Hair_Texture || null,
          hair_problem: hairConsultation?.Hair_Problem || null,
          hair_any_issues: hairConsultation?.any_issues || null,
          has_body_consultation: !!bodyConsultation,
          body_consultation_status:
            bodyConsultation?.consultation_status || null,
          body_wondura_done_at: bodyConsultation?.wondura_done_at || null,
          body_completed_at: bodyConsultation?.completed_at || null,
          body_processing_time: calculateProcessingTime(
            bodyConsultation?.wondura_done_at,
            bodyConsultation?.completed_at
          ),
          body_analysis: bodyConsultation?.detailed_diagnosis || "Pending",
          body_derma_output:
            bodyConsultation?.dermatologist_analysis || "Pending",
          body_routine_products: bodyRoutineProducts || null,
          body_budget: bodyConsultation?.budget || null,
          body_routine_output: bodyRoutineProducts ? "Generated" : "Pending",
          body_concerns: bodyConsultation?.body_concerns || null,
          body_relationship_status:
            bodyConsultation?.relationship_status || null,
          body_skin_sensitivity: bodyConsultation?.skin_sensitivity || null,
          body_additional_sensitivity:
            bodyConsultation?.additional_sensitivity || null,
          body_sensitivity_details: buildSensitivityDetails(
            bodyConsultation?.skin_sensitivity,
            bodyConsultation?.additional_sensitivity
          ),
          updated_at: formatAdminDateTime(
            latestUpdatedAt > 0 ? latestUpdatedAt : consultation?.updated_at
          ),
        };
      });

      const shouldFetchTotal = fetchAll || includeTotal;
      const totalCount = shouldFetchTotal
        ? fetchAll
          ? transformedData.length
          : await this._getDashboardTotalCount({
              dateRange,
              filterStatus,
              userRole,
              username,
              searchContext,
              searchResolution,
              countMode,
            })
        : null;

      const fallbackTotal = fetchAll
        ? transformedData.length
        : Math.min(
            (pageNumber - 1) * safePageSize +
              transformedData.length +
              (hasMoreRows ? 1 : 0),
            DASHBOARD_INTERFACE_RESULT_LIMIT
          );

      const stats = includeStats
        ? await this._getDashboardStats({
            searchTerm,
            dateRange,
            filterStatus,
            userRole,
            username,
            searchContext,
            searchResolution,
            countMode,
          })
        : null;

      console.log(
        `✅ Dashboard data fetched: ${transformedData.length} records (Role: ${userRole})`
      );

      const minimumKnownTotal = Math.min(
        hasMoreRows
          ? pageNumber * safePageSize + 1
          : (pageNumber - 1) * safePageSize + transformedData.length,
        DASHBOARD_INTERFACE_RESULT_LIMIT
      );

      const resolvedTotal = (() => {
        if (typeof totalCount === "number" && !Number.isNaN(totalCount)) {
          const cappedCount = capDashboardInterfaceTotal(totalCount);
          return Math.max(cappedCount || 0, minimumKnownTotal);
        }

        return Math.max(fallbackTotal, minimumKnownTotal);
      })();

      return {
        data: transformedData,
        total: shouldFetchTotal ? resolvedTotal : null,
        page: pageNumber,
        pageSize: safePageSize,
        stats,
        error: null,
      };
    } catch (error) {
      console.error("❌ Error fetching dashboard data:", error);
      return {
        data: null,
        error: new Error(extractErrorMessage(error)),
        total: 0,
        stats: null,
      };
    }
  }

  static _applyDashboardBaseFilters(
    query,
    {
      dateRange,
      filterStatus,
      userRole,
      username,
      applyPharmacistAssignmentScope = true,
    }
  ) {
    let nextQuery = query;

    if (dateRange?.from) {
      nextQuery = nextQuery.gte("created_at", dateRange.from);
    }
    if (dateRange?.to) {
      nextQuery = nextQuery.lte("created_at", dateRange.to);
    }
    if (
      applyPharmacistAssignmentScope &&
      userRole === "pharmacist" &&
      username
    ) {
      nextQuery = nextQuery.eq("assigned_pharmacist", username);
    }
    if (filterStatus !== "all") {
      nextQuery = nextQuery.eq("consultation_status", filterStatus);
    }

    return nextQuery;
  }

  static _applyDashboardFilters(
    query,
    {
      dateRange,
      filterStatus,
      userRole,
      username,
      searchContext,
      searchResolution = null,
    }
  ) {
    const bypassPharmacistAssignmentScope = Boolean(
      userRole === "pharmacist" && searchContext?.search
    );

    let nextQuery = this._applyDashboardBaseFilters(query, {
      dateRange,
      filterStatus,
      userRole,
      username,
      applyPharmacistAssignmentScope: !bypassPharmacistAssignmentScope,
    });

    const searchFilterState = getDashboardSearchFilterState({
      searchContext,
      searchResolution,
    });

    if (searchContext?.search) {
      if (searchContext.isUUID) {
        nextQuery = nextQuery.eq("user_id", searchContext.search);
      } else if (Array.isArray(searchFilterState.matchedUserIds)) {
        if (searchFilterState.matchedUserIds.length > 0) {
          nextQuery = nextQuery.in("user_id", searchFilterState.matchedUserIds);
        }
      } else if (searchContext.isTextSearch && !searchFilterState.shouldAbort) {
        nextQuery = nextQuery.or(`phone.ilike.%${searchContext.search}%`, {
          foreignTable: "users",
        });
      }
    }

    return nextQuery;
  }

  static async _resolveDashboardSearchUserIds(
    searchContext,
    { dateRange = null, filterStatus = "all", userRole, username } = {}
  ) {
    if (!searchContext?.isTextSearch) {
      return createDashboardSearchResolution();
    }

    const resolveByScopedConsultationScan = async () => {
      const normalizedSearch = normalizeDashboardPhoneSearchValue(
        searchContext.search
      );

      if (!normalizedSearch) {
        return createDashboardSearchResolution({
          status: "resolved",
          matchedUserIds: [],
          source: "client_side_scan_empty_search",
        });
      }

      let query = supabase
        .from("user_consultations")
        .select("user_id, users(id, phone)")
        .not("user_id", "is", null);

      query = this._applyDashboardBaseFilters(query, {
        dateRange,
        filterStatus,
        userRole,
        username,
        applyPharmacistAssignmentScope: false,
      });

      const { data, error } = await query.limit(20000);

      if (error) {
        console.warn(
          "⚠️ Failed scoped consultation scan for phone search:",
          error
        );
        return createDashboardSearchResolution({
          status: "unavailable",
          matchedUserIds: null,
          source: "client_side_scan_error",
          errorMessage:
            "Phone search is temporarily unavailable for this admin session.",
        });
      }

      const matchedUserIds = [
        ...new Set(
          (data || [])
            .filter((item) => {
              const user = Array.isArray(item?.users) ? item.users[0] : item?.users;
              const normalizedPhone = normalizeDashboardPhoneSearchValue(
                user?.phone
              );
              return (
                item?.user_id &&
                normalizedPhone &&
                normalizedPhone.includes(normalizedSearch)
              );
            })
            .map((item) => item.user_id)
        ),
      ];

      return createDashboardSearchResolution({
        status: "resolved",
        matchedUserIds,
        source: "client_side_scan",
      });
    };

    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        "search_dashboard_user_ids_by_phone",
        {
          p_search_term: searchContext.search,
          p_date_from: dateRange?.from || null,
          p_date_to: dateRange?.to || null,
          p_filter_status: filterStatus || "all",
        }
      );

      if (!rpcError) {
        const matchedUserIds = [
          ...new Set(
            (rpcData || []).map((item) => item.user_id).filter(Boolean)
          ),
        ];

        if (matchedUserIds.length > 0) {
          return createDashboardSearchResolution({
            status: "resolved",
            matchedUserIds,
            source: "rpc",
          });
        }

        return await resolveByScopedConsultationScan();
      }

      console.warn(
        "⚠️ RPC phone search unavailable, falling back to scoped consultation scan:",
        rpcError
      );

      return await resolveByScopedConsultationScan();
    } catch (error) {
      console.warn("⚠️ Unexpected dashboard search lookup failure:", error);

      return await resolveByScopedConsultationScan();
    }
  }

  static async _getDashboardTotalCount({
    dateRange,
    filterStatus,
    userRole,
    username,
    searchContext,
    searchResolution,
    countMode = "exact",
  }) {
    try {
      const searchFilterState = getDashboardSearchFilterState({
        searchContext,
        searchResolution,
      });

      if (searchFilterState.shouldAbort) {
        return null;
      }

      if (searchFilterState.isResolvedEmpty) {
        return 0;
      }

      const modesToTry =
        countMode === "exact" ? ["exact", "planned"] : [countMode];

      for (const mode of modesToTry) {
        let query = supabase
          .from("user_consultations")
          .select("id", { count: mode, head: true });

        query = this._applyDashboardFilters(query, {
          dateRange,
          filterStatus,
          userRole,
          username,
          searchContext,
          searchResolution,
        });

        const { count, error } = await query;

        if (error) {
          if (mode === "exact" && isStatementTimeoutError(error)) {
            console.warn(
              "⚠️ Exact dashboard total count timed out. Falling back to planned count."
            );
            continue;
          }

          console.warn("⚠️ Dashboard total count failed:", error);
          return null;
        }

        return count || 0;
      }

      return null;
    } catch (error) {
      console.warn("⚠️ Unexpected dashboard total count failure:", error);
      return null;
    }
  }

  static async _fetchDashboardContactMaps({ consultationIds, userIds }) {
    const contactIdByConsultationMap = {};
    const contactIdByUserMap = {};

    const [consultationMappings, userMappings] = await Promise.all([
      consultationIds.length > 0
        ? supabase
            .from("consultation_channel_mappings")
            .select("consultation_id, contact_id, updated_at")
            .in("consultation_id", consultationIds)
            .order("updated_at", { ascending: false })
        : Promise.resolve({ data: [], error: null }),
      userIds.length > 0
        ? supabase
            .from("consultation_channel_mappings")
            .select("user_id, contact_id, updated_at")
            .in("user_id", userIds)
            .order("updated_at", { ascending: false })
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (consultationMappings.error) {
      console.warn(
        "⚠️ Failed to fetch consultation channel mappings:",
        consultationMappings.error
      );
    } else {
      (consultationMappings.data || []).forEach((mapping) => {
        if (
          mapping.consultation_id &&
          !contactIdByConsultationMap[mapping.consultation_id]
        ) {
          contactIdByConsultationMap[mapping.consultation_id] =
            mapping.contact_id;
        }
      });
    }

    if (userMappings.error) {
      console.warn("⚠️ Failed to fetch user channel mappings:", userMappings.error);
    } else {
      (userMappings.data || []).forEach((mapping) => {
        if (mapping.user_id && !contactIdByUserMap[mapping.user_id]) {
          contactIdByUserMap[mapping.user_id] = mapping.contact_id;
        }
      });
    }

    return { contactIdByConsultationMap, contactIdByUserMap };
  }

  static async _fetchDashboardSubrecords(consultationIds, table) {
    const ids = [...new Set((consultationIds || []).filter(Boolean))];
    const recordsMap = {};

    if (ids.length === 0) {
      return recordsMap;
    }

    const chunkSize = 100;

    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      const { data, error } = await supabase
        .from(table)
        .select("*")
        .in("user_consultation_id", chunk);

      if (error) {
        console.error(`❌ Error fetching ${table}:`, error);
        continue;
      }

      (data || []).forEach((item) => {
        if (item?.user_consultation_id && !recordsMap[item.user_consultation_id]) {
          recordsMap[item.user_consultation_id] = item;
        }
      });
    }

    return recordsMap;
  }

  static async _fetchDashboardFallbackImages(userIds) {
    const ids = [...new Set((userIds || []).filter(Boolean))];
    const imageByUserMap = {};

    if (ids.length === 0) {
      return imageByUserMap;
    }

    const { data, error } = await supabase
      .from("consultation_images")
      .select(
        "id, user_id, consultation_id, image_path, image_type, upload_timestamp"
      )
      .in("user_id", ids)
      .order("upload_timestamp", { ascending: false });

    if (error) {
      console.warn("⚠️ Failed to fetch fallback consultation images:", error);
      return imageByUserMap;
    }

    (data || []).forEach((image) => {
      if (image?.user_id && !imageByUserMap[image.user_id]) {
        imageByUserMap[image.user_id] = image;
      }
    });

    return imageByUserMap;
  }

  static async _getPharmacistCurrentConsultationCount({ filters = {} } = {}) {
    try {
      const search = (filters.userSearch || "").trim();
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const isTextSearch = Boolean(search && !uuidRegex.test(search));

      let query = supabase
        .from("user_consultations")
        .select("id", { count: "exact", head: true });

      if (filters.pharmacistUsername && filters.pharmacistUsername.trim() !== "all") {
        query = query.eq("assigned_pharmacist", filters.pharmacistUsername.trim());
      }

      if (filters.status && filters.status.trim() !== "all") {
        query = query.eq("consultation_status", filters.status.trim());
      }

      if (filters.outcome && filters.outcome.trim() !== "all") {
        query = query.eq("consultation_outcome", filters.outcome.trim());
      }

      if (filters.dateRange?.from) {
        query = query.gte("created_at", filters.dateRange.from);
      }
      if (filters.dateRange?.to) {
        query = query.lte("created_at", filters.dateRange.to);
      }

      if (search) {
        if (isTextSearch) {
          query = query.or("name.ilike.%" + search + "%,phone.ilike.%" + search + "%", {
            foreignTable: "users",
          });
        } else {
          query = query.or(`id.eq.${search},user_id.eq.${search}`);
        }
      }

      const { count, error } = await query;

      if (error) {
        console.warn(
          "⚠️ Failed to fetch current consultation count for assignment logs:",
          error
        );
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.warn("⚠️ Unexpected current consultation count failure:", error);
      return 0;
    }
  }

  static async _getDashboardStats({
    searchTerm = "",
    dateRange = null,
    filterStatus = "all",
    userRole,
    username,
    searchContext = null,
    searchResolution = null,
    countMode = "exact",
  }) {
    try {
      const resolvedSearchContext =
        searchContext || getDashboardSearchContext(searchTerm);
      const searchFilterState = getDashboardSearchFilterState({
        searchContext: resolvedSearchContext,
        searchResolution,
      });

      if (searchFilterState.shouldAbort) {
        return null;
      }

      if (searchFilterState.isResolvedEmpty) {
        return buildEmptyDashboardStats();
      }

      const buildCountQuery = (extraFilter) => {
        let query = supabase
          .from("user_consultations")
          .select("id", { count: countMode, head: true });

        query = this._applyDashboardFilters(query, {
          dateRange,
          filterStatus,
          userRole,
          username,
          searchContext: resolvedSearchContext,
          searchResolution,
        });

        if (extraFilter) {
          query = query.eq(extraFilter.field, extraFilter.value);
        }

        return query;
      };

      const processingQuery = this._applyDashboardFilters(
        supabase
          .from("user_consultations")
          .select("wondura_done_at, completed_at"),
        {
          dateRange,
          filterStatus,
          userRole,
          username,
          searchContext: resolvedSearchContext,
          searchResolution,
        }
      )
        .not("wondura_done_at", "is", null)
        .not("completed_at", "is", null);

      const [
        totalResult,
        pendingResult,
        wonduraDoneResult,
        completedResult,
        paidResult,
        hotLeadResult,
        coldLeadResult,
        processingResult,
      ] = await Promise.all([
        buildCountQuery(),
        buildCountQuery({ field: "consultation_status", value: "pending" }),
        buildCountQuery({
          field: "consultation_status",
          value: "wondura_done",
        }),
        buildCountQuery({ field: "consultation_status", value: "completed" }),
        buildCountQuery({ field: "consultation_outcome", value: "paid" }),
        buildCountQuery({
          field: "consultation_outcome",
          value: "hot_lead",
        }),
        buildCountQuery({
          field: "consultation_outcome",
          value: "cold_lead",
        }),
        processingQuery,
      ]);

      const countErrors = [
        totalResult.error,
        pendingResult.error,
        wonduraDoneResult.error,
        completedResult.error,
        paidResult.error,
        hotLeadResult.error,
        coldLeadResult.error,
        processingResult.error,
      ].filter(Boolean);

      if (countErrors.length > 0) {
        if (userRole === "pharmacist" && isStatementTimeoutError(countErrors[0])) {
          console.warn(
            "⚠️ Pharmacist dashboard stats timed out. Returning null stats without failing the dashboard."
          );
          return null;
        }
        throw countErrors[0];
      }

      const processingRows = processingResult.data || [];
      let avgProcessingTime = 0;

      if (processingRows.length > 0) {
        const totalMinutes = processingRows.reduce((sum, item) => {
          const startTime = new Date(item.wondura_done_at);
          const endTime = new Date(item.completed_at);
          return sum + Math.floor((endTime - startTime) / 60000);
        }, 0);

        avgProcessingTime = Math.round(totalMinutes / processingRows.length);
      }

      const completed = completedResult.count || 0;
      const paidCount = paidResult.count || 0;

      return {
        totalConsultations: totalResult.count || 0,
        pending: pendingResult.count || 0,
        wonduraDone: wonduraDoneResult.count || 0,
        completed,
        paidCount,
        hotLeadCount: hotLeadResult.count || 0,
        coldLeadCount: coldLeadResult.count || 0,
        avgProcessingTime,
        conversionRate:
          completed > 0 ? Math.round((paidCount / completed) * 100) : 0,
      };
    } catch (error) {
      console.error("❌ Failed to calculate dashboard stats:", error);
      return null;
    }
  }

  // Get image URL for display - with 10 minute expiry and auto-refresh
  static async getImageUrl(imagePath) {
    if (!imagePath) return null;

    try {
      const rawPath = String(imagePath).replace(/^\/+/, "");
      const normalizedPath = rawPath.replace(/^consultation-images\//, "");
      const candidatePaths = Array.from(
        new Set([rawPath, normalizedPath].filter(Boolean))
      );

      for (const candidatePath of candidatePaths) {
        const { data: signedUrlData, error: signedUrlError } =
          await supabase.storage
            .from("consultation-images")
            .createSignedUrl(candidatePath, 600); // 10 minutes expiry

        if (signedUrlData?.signedUrl) {
          return signedUrlData.signedUrl;
        }

        console.warn("⚠️ Signed URL attempt failed:", {
          imagePath: candidatePath,
          message: signedUrlError?.message,
          statusCode: signedUrlError?.statusCode,
          error: signedUrlError?.error,
        });
      }

      console.error("❌ No signed URL returned for image path:", imagePath);
      return null;
    } catch (error) {
      console.error("❌ Error getting image URL:", error);
      return null;
    }
  }

  // ============================================
  // CONSULTATION MANAGEMENT METHODS
  // ============================================

  // Update consultation outcome (PAID/HOT_LEAD/COLD_LEAD)
  static async updateConsultationOutcome(consultationId, outcome) {
    try {
      const currentUser = this.getCurrentUser();
      const userRole = currentUser?.role;
      const username = currentUser?.username;

      // Coordinators cannot set outcomes (read-only role)
      if (userRole === "coordinator") {
        return {
          success: false,
          error: "Coordinators cannot modify consultation outcomes",
        };
      }

      // Validate outcome
      const validOutcomes = ["paid", "hot_lead", "cold_lead"];
      if (!validOutcomes.includes(outcome)) {
        return { success: false, error: "Invalid outcome" };
      }

      // If pharmacist, verify the consultation is assigned to them
      if (userRole === "pharmacist") {
        const { data: consultationCheck, error: checkError } = await supabase
          .from("user_consultations")
          .select("assigned_pharmacist")
          .eq("id", consultationId)
          .single();

        if (checkError) {
          console.error("❌ Consultation check error:", checkError);
          return { success: false, error: "Consultation not found" };
        }

        if (consultationCheck.assigned_pharmacist !== username) {
          console.warn(
            "🚫 Pharmacist attempted to update unassigned consultation:",
            consultationId
          );
          return {
            success: false,
            error: "You can only update consultations assigned to you",
          };
        }
      }

      // Update consultation with outcome, status, and completion timestamp
      const { data, error } = await supabase
        .from("user_consultations")
        .update({
          consultation_outcome: outcome,
          consultation_status: "completed",
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", consultationId)
        .select()
        .single();

      if (error) {
        console.error("❌ Consultation update error:", error);
        return { success: false, error };
      }

      console.log("✅ Consultation outcome updated:", consultationId, outcome);
      return { success: true, data };
    } catch (error) {
      console.error("❌ Error updating consultation:", error);
      return { success: false, error };
    }
  }

  static async getConsultationModelCorrectionOptions(scope = "skin") {
    try {
      const { data, error } = await supabase
        .from("consultation_model_correction_options")
        .select("id, scope, label, normalized_key, is_active, created_at")
        .eq("scope", scope)
        .eq("is_active", true)
        .order("label", { ascending: true });

      if (error) {
        console.error("❌ Error fetching model correction options:", error);
        return { success: false, error: extractErrorMessage(error) };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error("❌ Error in getConsultationModelCorrectionOptions:", error);
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  static async createConsultationModelCorrectionOption({
    label,
    scope = "skin",
  }) {
    try {
      const currentUser = this.getCurrentUser();
      const userRole = currentUser?.role;

      if (userRole !== "super_admin") {
        return { success: false, error: "Unauthorized - super admin access required" };
      }

      const trimmedLabel = String(label || "").trim();
      if (!trimmedLabel) {
        return { success: false, error: "Option label is required" };
      }

      const normalizedKey = normalizeCorrectionOptionKey(trimmedLabel);
      if (!normalizedKey) {
        return { success: false, error: "Invalid option label" };
      }

      const insertPayload = {
        scope,
        label: trimmedLabel,
        normalized_key: normalizedKey,
        is_active: true,
        created_by: currentUser?.id || null,
      };

      const { data, error } = await supabase
        .from("consultation_model_correction_options")
        .insert(insertPayload)
        .select("id, scope, label, normalized_key, is_active, created_at")
        .single();

      if (error) {
        if (error.code === "23505") {
          const { data: existingOption, error: existingError } = await supabase
            .from("consultation_model_correction_options")
            .select("id, scope, label, normalized_key, is_active, created_at")
            .eq("scope", scope)
            .eq("normalized_key", normalizedKey)
            .single();

          if (existingError) {
            return { success: false, error: extractErrorMessage(existingError) };
          }

          return { success: true, data: existingOption };
        }

        console.error("❌ Error creating model correction option:", error);
        return { success: false, error: extractErrorMessage(error) };
      }

      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in createConsultationModelCorrectionOption:", error);
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  static async saveConsultationModelReviewSlot(
    consultationId,
    slotKey,
    slotReview,
    existingReview = null,
    metadata = {}
  ) {
    try {
      const currentUser = this.getCurrentUser();
      const userRole = currentUser?.role;

      if (userRole !== "super_admin") {
        return { success: false, error: "Unauthorized - super admin access required" };
      }

      if (!consultationId) {
        return { success: false, error: "Consultation ID is required" };
      }

      if (!slotKey) {
        return { success: false, error: "Slot key is required" };
      }

      const nextReview = buildNextModelOutputReview(
        existingReview,
        slotKey,
        slotReview,
        {
          updatedAt: new Date().toISOString(),
          updatedBy: metadata.updatedBy || currentUser?.id || null,
        }
      );

      const { data, error } = await supabase.rpc(
        "patch_consultation_model_output_review",
        {
          p_consultation_id: consultationId,
          p_model_output_review: nextReview,
        }
      );

      if (error) {
        console.error("❌ Error saving consultation model review:", error);
        return { success: false, error: extractErrorMessage(error) };
      }

      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in saveConsultationModelReviewSlot:", error);
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  // ============================================
  // CSV EXPORT & KPI METHODS
  // ============================================

  // Export data to CSV
  static exportToCSV(data) {
    const headers = [
      "User ID",
      "Consultation ID",
      "Name",
      "Phone",
      "Skin Type",
      "Relationship Status",
      "Budget",
      "Sensitivity Details",
      "Image Analysis",
      "Derma Output",
      "Routine Output",
      "Consultation Status",
      "Outcome",
      "Assigned Pharmacist",
      "Processing Time",
      "Updated At",
    ];

    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        [
          row.user_id,
          row.consultation_id || "N/A",
          `"${row.name}"`,
          row.phone,
          `"${row.skin_type}"`,
          `"${row.relationship_status}"`,
          `"${row.budget}"`,
          `"${row.sensitivity_details}"`,
          row.image_analysis,
          row.derma_output,
          row.routine_output,
          row.consultation_status,
          row.consultation_outcome || "N/A",
          row.assigned_pharmacist || "Unassigned",
          row.processing_time || "N/A",
          `"${row.updated_at}"`,
        ].join(",")
      ),
    ].join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `wonder_beauties_dashboard_${new Date().toISOString().split("T")[0]
      }.csv`;
    link.click();

    console.log("📥 CSV export completed");
  }

  // Calculate KPIs from dashboard data
  static calculateKPIs(data) {
    const completedConsultations = data.filter(
      (item) => item.consultation_status === "completed"
    );
    const wonduraDone = data.filter(
      (item) => item.consultation_status === "wondura_done"
    );
    const paidCount = completedConsultations.filter(
      (item) => item.consultation_outcome === "paid"
    ).length;
    const hotLeadCount = completedConsultations.filter(
      (item) => item.consultation_outcome === "hot_lead"
    ).length;
    const coldLeadCount = completedConsultations.filter(
      (item) => item.consultation_outcome === "cold_lead"
    ).length;

    // Calculate average processing time (in minutes)
    const consultationsWithTime = completedConsultations.filter(
      (item) => item.wondura_done_at && item.completed_at
    );

    let avgProcessingTime = 0;
    if (consultationsWithTime.length > 0) {
      const totalMinutes = consultationsWithTime.reduce((sum, item) => {
        const wonduraTime = new Date(item.wondura_done_at);
        const completedTime = new Date(item.completed_at);
        const diffMs = completedTime - wonduraTime;
        const diffMins = Math.floor(diffMs / 60000);
        return sum + diffMins;
      }, 0);
      avgProcessingTime = Math.round(
        totalMinutes / consultationsWithTime.length
      );
    }

    // Calculate conversion rate
    const conversionRate =
      completedConsultations.length > 0
        ? Math.round((paidCount / completedConsultations.length) * 100)
        : 0;

    return {
      totalConsultations: data.length,
      pending: data.filter((item) => item.consultation_status === "pending")
        .length,
      wonduraDone: wonduraDone.length,
      completed: completedConsultations.length,
      paidCount,
      hotLeadCount,
      coldLeadCount,
      avgProcessingTime, // in minutes
      conversionRate, // percentage
    };
  }

  // ============================================
  // PHARMACIST MANAGEMENT METHODS
  // ============================================

  // Get all pharmacists with availability info (for re-assignment dropdown)
  static async getAllPharmacists() {
    try {
      const currentUser = this.getCurrentUser();
      const canAccess =
        currentUser?.role === "super_admin" ||
        currentUser?.role === "coordinator";
      if (!canAccess) {
        return { success: false, error: "Unauthorized" };
      }

      // Query admin_users + round_robin for pharmacists
      const { data, error } = await supabase
        .from("admin_users")
        .select(
          `
          id,
          username,
          role,
          is_active,
          round_robin (
            is_online,
            daily_limit,
            daily_counter,
            hourly_limit,
            hourly_dispatch_count,
            pharmacist_type
          )
        `
        )
        .eq("role", "pharmacist")
        .eq("is_active", true)
        .order("username");

      if (error) {
        console.error("❌ Error fetching pharmacists:", error);
        return { success: false, error: error.message };
      }

      // Transform data to flatten structure
      const pharmacists = data.map((p) => {
        // Handle both array and object response formats
        const rrData = Array.isArray(p.round_robin)
          ? p.round_robin[0]
          : p.round_robin;

        const pharmacistType = rrData?.pharmacist_type || "full_time";
        const dailyCounter = Number(rrData?.daily_counter || 0);
        const dailyLimit = Number(rrData?.daily_limit ?? (pharmacistType === "freelance" ? dailyCounter : 50));

        return {
          id: p.id,
          username: p.username,
          is_online: rrData?.is_online || false,
          daily_limit: dailyLimit,
          daily_counter: dailyCounter,
          hourly_limit: rrData?.hourly_limit || 12,
          hourly_dispatch_count: rrData?.hourly_dispatch_count || 0,
          pharmacist_type: pharmacistType,
          is_at_limit: dailyCounter >= dailyLimit,
        };
      });

      console.log("✅ Pharmacists fetched:", pharmacists.length);
      return { success: true, data: pharmacists };
    } catch (error) {
      console.error("❌ Error in getAllPharmacists:", error);
      return { success: false, error: error.message };
    }
  }

  // Log availability changes to staff_availability_log table
  static async logAvailabilityChange({
    adminUserId,
    action,
    oldValue,
    newValue,
    notes,
  }) {
    try {
      const currentUser = this.getCurrentUser();

      const { error } = await supabase.from("staff_availability_log").insert({
        admin_user_id: adminUserId,
        action: action,
        old_value: String(oldValue),
        new_value: String(newValue),
        changed_by: currentUser.id,
        changed_at: new Date().toISOString(),
        notes: notes,
      });

      if (error) {
        console.error("❌ Logging error:", error);
      }
    } catch (error) {
      console.error("❌ Error in logAvailabilityChange:", error);
    }
  }

  static _normalizeRoundRobinJoin(roundRobinValue) {
    return Array.isArray(roundRobinValue)
      ? roundRobinValue[0]
      : roundRobinValue;
  }

  static async getRoundRobinStatusForAdminUser(adminUserId) {
    try {
      if (!adminUserId) {
        return { success: false, error: "Admin user ID is required" };
      }

      const { data, error } = await supabase
        .from("admin_users")
        .select("username, round_robin(id, is_online, is_active, pharmacist_type, daily_counter, daily_limit, hourly_limit, hourly_dispatch_count)")
        .eq("id", adminUserId)
        .single();

      if (error || !data) {
        return {
          success: false,
          error: extractErrorMessage(error) || "Could not fetch round robin status",
        };
      }

      const rrData = this._normalizeRoundRobinJoin(data.round_robin);
      if (!rrData?.id) {
        return { success: false, error: "No round robin record found" };
      }

      return {
        success: true,
        username: data.username,
        roundRobinId: rrData.id,
        is_online: Boolean(rrData.is_online),
        is_active: Boolean(rrData.is_active ?? true),
        pharmacist_type: rrData.pharmacist_type || "full_time",
        daily_counter: rrData.daily_counter || 0,
        daily_limit: rrData.daily_limit || 0,
        hourly_limit: rrData.hourly_limit || 0,
        hourly_dispatch_count: rrData.hourly_dispatch_count || 0,
      };
    } catch (error) {
      console.error("❌ Error in getRoundRobinStatusForAdminUser:", error);
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  static async verifyRoundRobinStatus(adminUserId, expectedOnlineStatus) {
    const verification = await this.getRoundRobinStatusForAdminUser(adminUserId);
    if (!verification.success) {
      return {
        success: false,
        error: `Could not verify saved status: ${verification.error || "unknown error"}`,
      };
    }

    if (verification.is_online !== expectedOnlineStatus) {
      return {
        success: false,
        error: `Supabase still shows this pharmacist as ${verification.is_online ? "online" : "offline"}.`,
        is_online: verification.is_online,
        roundRobinId: verification.roundRobinId,
        username: verification.username,
      };
    }

    return {
      success: true,
      is_online: verification.is_online,
      roundRobinId: verification.roundRobinId,
      username: verification.username,
    };
  }

  // Toggle pharmacist online/offline status
  static async togglePharmacistStatus(adminUserId, newOnlineStatus) {
    try {
      const currentUser = this.getCurrentUser();
      const canAccess =
        currentUser?.role === "super_admin" ||
        currentUser?.role === "coordinator";

      if (!canAccess) {
        return { success: false, error: "Unauthorized" };
      }

      const currentStatus = await this.getRoundRobinStatusForAdminUser(adminUserId);
      if (!currentStatus.success) {
        return { success: false, error: currentStatus.error || "Pharmacist not found" };
      }

      const oldStatus = currentStatus.is_online;
      const roundRobinId = currentStatus.roundRobinId;
      const pharmacistUsername = currentStatus.username;

      const pharmacistType = currentStatus.pharmacist_type || "full_time";
      const updates = { is_online: newOnlineStatus };

      // Freelance unused requested capacity should not carry across offline sessions.
      // If she requested +3, got 0 assigned, then goes offline, next online request must
      // start from current daily_counter again instead of keeping old remaining capacity.
      if (pharmacistType === "freelance" && !newOnlineStatus) {
        updates.daily_limit = Number(currentStatus.daily_counter || 0);
      }

      // Update round_robin status
      const { error: updateError } = await supabase
        .from("round_robin")
        .update(updates)
        .eq("admin_user_id", adminUserId);

      if (updateError) {
        console.error("❌ Error updating status:", updateError);
        return { success: false, error: updateError.message };
      }

      const verification = await this.verifyRoundRobinStatus(adminUserId, newOnlineStatus);
      if (!verification.success) {
        console.error("❌ Status verification failed:", verification.error);
        return {
          success: false,
          error: verification.error || "Status update could not be verified",
          is_online: verification.is_online,
        };
      }

      // Log the change
      const action = newOnlineStatus ? "went_online" : "went_offline";
      const notes = `Status toggled by ${currentUser.role} ${currentUser.username} for pharmacist ${pharmacistUsername}`;

      await this.logAvailabilityChange({
        adminUserId,
        action,
        oldValue: oldStatus,
        newValue: newOnlineStatus,
        notes,
      });

      console.log(
        `✅ Pharmacist ${pharmacistUsername} status changed to ${newOnlineStatus ? "online" : "offline"
        }`
      );

      // If pharmacist just came online (was offline → now online), trigger queue dispatch webhook
      // Fire-and-forget: don't block on webhook result, n8n handles eligibility checks
      if (!oldStatus && newOnlineStatus && roundRobinId && currentStatus.pharmacist_type !== "freelance") {
        console.log(
          `📤 Pharmacist ${pharmacistUsername} came online, triggering queue dispatch webhook...`
        );
        WebhookService.callPharmacistOnlineWebhook(roundRobinId)
          .then((result) => {
            if (result.success) {
              console.log(`✅ Queue dispatch webhook succeeded:`, result.data);
            } else {
              console.warn(`⚠️ Queue dispatch webhook failed:`, result.error);
            }
          })
          .catch((err) => {
            console.warn(`⚠️ Queue dispatch webhook error:`, err.message);
          });
      }

      return { success: true, verified: true, is_online: verification.is_online, pharmacist_type: currentStatus.pharmacist_type || "full_time" };
    } catch (error) {
      console.error("❌ Error in togglePharmacistStatus:", error);
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  // Toggle own online/offline status (for pharmacists to toggle themselves)
  static async toggleOwnOnlineStatus(newOnlineStatus, options = {}) {
    try {
      const currentUser = this.getCurrentUser();

      if (!currentUser) {
        return { success: false, error: "Not authenticated" };
      }

      const currentStatus = await this.getRoundRobinStatusForAdminUser(currentUser.id);
      if (!currentStatus.success) {
        return { success: false, error: currentStatus.error || "User not found" };
      }

      const oldStatus = currentStatus.is_online;
      const roundRobinId = currentStatus.roundRobinId;

      const pharmacistType = currentStatus.pharmacist_type || "full_time";
      const updates = { is_online: newOnlineStatus };

      if (pharmacistType === "freelance" && newOnlineStatus) {
        const additionalLimit = parseInt(options.additionalLimit, 10);
        const mode = options.mode === "more" ? "more" : "initial";
        const allowed = mode === "more" ? [1, 2, 3] : [1, 2, 3, 4];
        if (!allowed.includes(additionalLimit)) {
          return {
            success: false,
            error: mode === "more"
              ? "Choose 1, 2, or 3 additional consultations"
              : "Choose 1, 2, 3, or 4 consultations to start",
          };
        }

        // Requested capacity is always relative to consultations already assigned today,
        // not the previous daily_limit. This prevents offline unused capacity from stacking.
        updates.daily_limit = Number(currentStatus.daily_counter || 0) + additionalLimit;
      }

      if (pharmacistType === "freelance" && !newOnlineStatus) {
        // Clear unused requested capacity when freelance goes offline.
        // Example: counter=0, limit=3, offline => limit=0. Next +3 online stays 3, not 6.
        updates.daily_limit = Number(currentStatus.daily_counter || 0);
      }

      // Update round_robin status
      const { error: updateError } = await supabase
        .from("round_robin")
        .update(updates)
        .eq("admin_user_id", currentUser.id);

      if (updateError) {
        console.error("❌ Error updating own status:", updateError);
        return { success: false, error: updateError.message };
      }

      const verification = await this.verifyRoundRobinStatus(currentUser.id, newOnlineStatus);
      if (!verification.success) {
        console.error("❌ Own status verification failed:", verification.error);
        return {
          success: false,
          error: verification.error || "Status update could not be verified",
          is_online: verification.is_online,
        };
      }

      // Log the change
      const action = newOnlineStatus ? "went_online" : "went_offline";
      await this.logAvailabilityChange({
        adminUserId: currentUser.id,
        action,
        oldValue: oldStatus,
        newValue: newOnlineStatus,
        notes: `Self-toggled by ${currentUser.username}`,
      });

      console.log(
        `✅ ${currentUser.username} status changed to ${newOnlineStatus ? "online" : "offline"
        }`
      );

      // If coming online, trigger queue dispatch webhook (fire-and-forget)
      if (!oldStatus && newOnlineStatus && roundRobinId && currentStatus.pharmacist_type !== "freelance") {
        console.log(
          `📤 ${currentUser.username} came online, triggering queue dispatch webhook...`
        );
        WebhookService.callPharmacistOnlineWebhook(roundRobinId)
          .then((result) => {
            if (result.success) {
              console.log(`✅ Queue dispatch webhook succeeded:`, result.data);
            } else {
              console.warn(`⚠️ Queue dispatch webhook failed:`, result.error);
            }
          })
          .catch((err) => {
            console.warn(`⚠️ Queue dispatch webhook error:`, err.message);
          });
      }

      return { success: true, verified: true, is_online: verification.is_online, pharmacist_type: currentStatus.pharmacist_type || "full_time" };
    } catch (error) {
      console.error("❌ Error in toggleOwnOnlineStatus:", error);
      return { success: false, error: extractErrorMessage(error) };
    }
  }

  // Get own online status (for pharmacists)
  static async getOwnOnlineStatus() {
    try {
      const currentUser = this.getCurrentUser();

      if (!currentUser) {
        return { success: false, error: "Not authenticated" };
      }

      const { data, error } = await supabase
        .from("admin_users")
        .select("round_robin(id, is_online, pharmacist_type, daily_counter, daily_limit, hourly_limit, hourly_dispatch_count, last_dispatch_at)")
        .eq("id", currentUser.id)
        .single();

      if (error || !data) {
        return { success: false, error: "Could not fetch status" };
      }

      const rrData = this._normalizeRoundRobinJoin(data.round_robin);
      return {
        success: true,
        roundRobinId: rrData?.id || null,
        is_online: Boolean(rrData?.is_online),
        pharmacist_type: rrData?.pharmacist_type || "full_time",
        daily_counter: rrData?.daily_counter || 0,
        daily_limit: rrData?.daily_limit || 0,
        hourly_limit: rrData?.hourly_limit || 0,
        hourly_dispatch_count: rrData?.hourly_dispatch_count || 0,
        last_dispatch_at: rrData?.last_dispatch_at || null,
      };
    } catch (error) {
      console.error("❌ Error in getOwnOnlineStatus:", error);
      return { success: false, error: error.message };
    }
  }

  // Update pharmacist dispatch limits.
  // Daily limit is super-admin only, hourly limit can be managed by super-admin/coordinator.
  static async updatePharmacistDispatchLimits(
    adminUserId,
    { dailyLimit, hourlyLimit } = {}
  ) {
    try {
      const currentUser = this.getCurrentUser();
      const canManageDaily = currentUser?.role === "super_admin";
      const canManageHourly =
        currentUser?.role === "super_admin" ||
        currentUser?.role === "coordinator";

      if (!canManageDaily && !canManageHourly) {
        return {
          success: false,
          error: "Unauthorized - Admin or Coordinator access required",
        };
      }

      const updates = {};

      if (dailyLimit !== undefined) {
        if (!canManageDaily) {
          return {
            success: false,
            error: "Unauthorized - Super Admin access required for daily limit",
          };
        }

        const parsedDailyLimit = parseInt(dailyLimit, 10);
        if (
          !Number.isInteger(parsedDailyLimit) ||
          parsedDailyLimit < 1 ||
          parsedDailyLimit > 999
        ) {
          return {
            success: false,
            error: "Daily limit must be between 1 and 999",
          };
        }

        updates.daily_limit = parsedDailyLimit;
      }

      if (hourlyLimit !== undefined) {
        if (!canManageHourly) {
          return {
            success: false,
            error:
              "Unauthorized - Admin or Coordinator access required for hourly limit",
          };
        }

        const parsedHourlyLimit = parseInt(hourlyLimit, 10);
        if (
          !Number.isInteger(parsedHourlyLimit) ||
          parsedHourlyLimit < 1 ||
          parsedHourlyLimit > 999
        ) {
          return {
            success: false,
            error: "Hourly limit must be between 1 and 999",
          };
        }

        updates.hourly_limit = parsedHourlyLimit;
      }

      if (Object.keys(updates).length === 0) {
        return {
          success: false,
          error: "No limit changes provided",
        };
      }

      // Get current limits and username
      const { data: userData, error: fetchError } = await supabase
        .from("admin_users")
        .select("username, round_robin(daily_limit, hourly_limit)")
        .eq("id", adminUserId)
        .single();

      if (fetchError || !userData) {
        return { success: false, error: "Pharmacist not found" };
      }

      // Handle both array and object response formats
      const rrData = Array.isArray(userData.round_robin)
        ? userData.round_robin[0]
        : userData.round_robin;
      const pharmacistUsername = userData.username;
      const oldDailyLimit = rrData?.daily_limit || 50;
      const oldHourlyLimit = rrData?.hourly_limit || 12;

      // Update limits
      const { error: updateError } = await supabase
        .from("round_robin")
        .update(updates)
        .eq("admin_user_id", adminUserId);

      if (updateError) {
        console.error("❌ Error updating dispatch limits:", updateError);
        return { success: false, error: updateError.message };
      }

      const changedLimits = [];
      if (updates.daily_limit !== undefined) {
        changedLimits.push(
          `daily limit changed from ${oldDailyLimit} to ${updates.daily_limit}`
        );
      }
      if (updates.hourly_limit !== undefined) {
        changedLimits.push(
          `hourly limit changed from ${oldHourlyLimit} to ${updates.hourly_limit}`
        );
      }
      const notes = `${changedLimits.join(
        " and "
      )} by ${currentUser.username} for pharmacist ${pharmacistUsername}`;

      await this.logAvailabilityChange({
        adminUserId,
        action: "dispatch_limits_updated",
        oldValue: JSON.stringify({
          daily_limit: oldDailyLimit,
          hourly_limit: oldHourlyLimit,
        }),
        newValue: JSON.stringify(updates),
        notes,
      });

      console.log(`✅ Dispatch limits for ${pharmacistUsername} updated`);
      return { success: true, updates };
    } catch (error) {
      console.error("❌ Error in updatePharmacistDispatchLimits:", error);
      return { success: false, error: error.message };
    }
  }

  // Update pharmacist daily limit (backward-compatible wrapper)
  static async updatePharmacistDailyLimit(adminUserId, newDailyLimit) {
    return this.updatePharmacistDispatchLimits(adminUserId, {
      dailyLimit: newDailyLimit,
    });
  }

  // Update pharmacist hourly limit
  static async updatePharmacistHourlyLimit(adminUserId, newHourlyLimit) {
    return this.updatePharmacistDispatchLimits(adminUserId, {
      hourlyLimit: newHourlyLimit,
    });
  }

  // Reset pharmacist daily counter to 0 (Super Admin only)
  static async resetPharmacistDailyCounter(adminUserId) {
    try {
      if (!this.isSuperAdmin()) {
        return {
          success: false,
          error: "Unauthorized - Super Admin access required",
        };
      }

      const currentUser = this.getCurrentUser();

      // Get current counter and username
      const { data: userData, error: fetchError } = await supabase
        .from("admin_users")
        .select("username, round_robin(daily_counter)")
        .eq("id", adminUserId)
        .single();

      if (fetchError || !userData) {
        return { success: false, error: "Pharmacist not found" };
      }

      // Handle both array and object response formats
      const rrData = Array.isArray(userData.round_robin)
        ? userData.round_robin[0]
        : userData.round_robin;
      const oldCounter = rrData?.daily_counter || 0;
      const pharmacistUsername = userData.username;

      // Reset counter and update timestamp
      const { error: updateError } = await supabase
        .from("round_robin")
        .update({
          daily_counter: 0,
          last_counter_reset: new Date().toISOString(),
        })
        .eq("admin_user_id", adminUserId);

      if (updateError) {
        console.error("❌ Error resetting counter:", updateError);
        return { success: false, error: updateError.message };
      }

      // Log the change
      const notes = `Daily counter manually reset from ${oldCounter} to 0 by ${currentUser.username} for pharmacist ${pharmacistUsername}`;

      await this.logAvailabilityChange({
        adminUserId,
        action: "counter_reset",
        oldValue: oldCounter,
        newValue: 0,
        notes,
      });

      console.log(`✅ Daily counter for ${pharmacistUsername} reset to 0`);
      return { success: true };
    } catch (error) {
      console.error("❌ Error in resetPharmacistDailyCounter:", error);
      return { success: false, error: error.message };
    }
  }

  // Re-assign consultation to different pharmacist (Super Admin and Coordinator)
  static async reassignConsultation(consultationId, newPharmacistUsername) {
    try {
      const currentUser = this.getCurrentUser();
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const reassignedByAdminId = currentUser?.id;
      const sessionIsHealthy = await this.verifySession({ logoutOnFailure: false });
      const sessionWarning = sessionIsHealthy
        ? ""
        : "Your session appears to be expired. We will still try this action, but you may need to log in again.";
      const sessionExpiredError = "Session expired. Please log in again and retry.";

      // Super admin or coordinator only
      if (!this.isSuperAdmin() && !this.isCoordinator()) {
        return {
          success: false,
          error: "Unauthorized - Super admin or coordinator access required",
        };
      }

      if (!reassignedByAdminId || !uuidRegex.test(reassignedByAdminId)) {
        return {
          success: false,
          error: "Invalid admin session. Please sign out and sign in again.",
        };
      }

      // Get current consultation data
      const { data: consultation, error: fetchError } = await supabase
        .from("user_consultations")
        .select("assigned_pharmacist")
        .eq("id", consultationId)
        .single();

      if (fetchError) {
        console.error("❌ Consultation fetch error:", fetchError);
        if (!sessionIsHealthy || this._isLikelySessionError(fetchError)) {
          return { success: false, error: sessionExpiredError, warning: sessionWarning, requiresLogin: true };
        }
        return { success: false, error: fetchError.message || "Failed to load consultation", warning: sessionWarning };
      }

      if (!consultation) {
        return { success: false, error: "Consultation not found" };
      }

      const oldPharmacist = consultation.assigned_pharmacist;

      // Prevent re-assigning to the same pharmacist
      if (oldPharmacist === newPharmacistUsername) {
        return {
          success: false,
          error: "Consultation is already assigned to this pharmacist",
        };
      }

      // Verify target pharmacist exists and is active
      const { data: targetPharmacist, error: pharmacistError } = await supabase
        .from("admin_users")
        .select("id, username, role, is_active")
        .eq("username", newPharmacistUsername)
        .eq("role", "pharmacist")
        .single();

      if (pharmacistError) {
        if (!sessionIsHealthy || this._isLikelySessionError(pharmacistError)) {
          return { success: false, error: sessionExpiredError, warning: sessionWarning, requiresLogin: true };
        }
        return { success: false, error: pharmacistError.message || "Failed to load pharmacist", warning: sessionWarning };
      }

      if (!targetPharmacist) {
        return { success: false, error: "Target pharmacist not found", warning: sessionWarning };
      }

      if (!targetPharmacist.is_active) {
        return {
          success: false,
          error: "Cannot assign to inactive pharmacist",
        };
      }

      // Update consultation with re-assignment data
      const { data: updated, error: updateError } = await supabase
        .from("user_consultations")
        .update({
          assigned_pharmacist: newPharmacistUsername,
          assignment_source: "dashboard",
          previous_pharmacist: oldPharmacist,
          reassigned_by: reassignedByAdminId,
          reassigned_at: new Date().toISOString(),
          assignment_changed: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", consultationId)
        .select()
        .single();

      if (updateError) {
        console.error("❌ Re-assignment update error:", updateError);
        if (!sessionIsHealthy || this._isLikelySessionError(updateError)) {
          return { success: false, error: sessionExpiredError, warning: sessionWarning, requiresLogin: true };
        }
        return { success: false, error: updateError.message, warning: sessionWarning };
      }

      console.log(
        `✅ Consultation ${consultationId} reassigned: ${oldPharmacist} → ${newPharmacistUsername} by ${currentUser.username}`
      );
      return {
        success: true,
        data: updated,
        warning: sessionWarning || null,
        message: `Successfully reassigned from ${oldPharmacist || "Unassigned"
          } to ${newPharmacistUsername}`,
      };
    } catch (error) {
      console.error("❌ Error in reassignConsultation:", error);
      if (this._isLikelySessionError(error)) {
        return { success: false, error: "Session expired. Please log in again and retry.", requiresLogin: true };
      }
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // USER MANAGEMENT METHODS (Super Admin Only)
  // ============================================
  // NOTE: These methods are temporarily disabled.
  // User creation and password reset must be done via Supabase Dashboard
  // until Edge Functions are implemented.
  // ============================================

  // Get all admin users with optional filters (read-only - works with RLS)
  static async getAllAdminUsers(filters = {}) {
    try {
      const currentUser = this.getCurrentUser();
      const canAccess = currentUser?.role === "super_admin" || currentUser?.role === "coordinator";

      if (!canAccess) {
        return { success: false, error: "Unauthorized" };
      }

      console.log("📊 Fetching admin users with filters:", filters);

      // Build query with filters
      let query = supabase
        .from("admin_users")
        .select(`
          id,
          username,
          role,
          is_active,
          created_at,
          email,
          round_robin (
            respondio_user_id,
            greeting_message,
            pharmacist_type
          )
        `);

      // Apply date range filter
      if (filters.dateRange?.from) {
        query = query.gte('created_at', filters.dateRange.from);
        // console.log("🔍 Filtering from date:", filters.dateRange.from);
      }
      if (filters.dateRange?.to) {
        query = query.lte('created_at', filters.dateRange.to);
        // console.log("🔍 Filtering to date:", filters.dateRange.to);
      }

      // Apply text filters with case-insensitive partial matching
      if (filters.username && filters.username.trim()) {
        query = query.ilike('username', `%${filters.username.trim()}%`);
        // console.log("🔍 Filtering username:", filters.username);
      }
      if (filters.email && filters.email.trim()) {
        query = query.ilike('email', `%${filters.email.trim()}%`);
        // console.log("🔍 Filtering email:", filters.email);
      }
      if (filters.userId && filters.userId.trim()) {
        // For UUID, we can use exact match or partial match
        query = query.ilike('id', `%${filters.userId.trim()}%`);
        // console.log("🔍 Filtering user ID:", filters.userId);
      }

      // Order and execute query
      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) {
        console.error("❌ Error fetching admin users:", error);
        return { success: false, error: error.message };
      }

      const normalizedData = (data || []).map((user) => {
        const roundRobin = Array.isArray(user.round_robin)
          ? user.round_robin[0]
          : user.round_robin;

        return {
          ...user,
          respondIoId: roundRobin?.respondio_user_id || "",
          greetingMessage: roundRobin?.greeting_message || "",
          pharmacistType: roundRobin?.pharmacist_type || "full_time",
        };
      });

      console.log("✅ Admin users fetched:", normalizedData.length, "records");
      return { success: true, data: normalizedData };
    } catch (error) {
      console.error("❌ Error in getAllAdminUsers:", error);
      return { success: false, error: error.message };
    }
  }

  // Create new admin user via Edge Function
  static async createAdminUser(username, email, role, respondIoId = null, greetingMessage = null, pharmacistType = "full_time") {
    try {
      if (!this.canManageUsers()) {
        return { success: false, error: 'Unauthorized' }
      }

      const normalizedEmail = String(email || '').trim().toLowerCase()
      if (!username || !normalizedEmail) {
        return { success: false, error: 'Username and email are required' }
      }

      console.log('✨ Creating new user via Edge Function:', username, role, normalizedEmail)

      const { data, error } = await supabase.functions.invoke('create-admin-user', {
        body: {
          email: normalizedEmail,
          username,
          role,
          respondIoId,
          greetingMessage,
          pharmacistType: role === "pharmacist" ? pharmacistType : null,
          redirectTo: `${window.location.origin}/admin/login`
        }
      })

      if (error) {
        console.error('� Edge function error:', error)
        return { success: false, error: error.message || 'Failed to create user via Edge Function' }
      }

      if (!data?.success) {
        return { success: false, error: data?.error || 'Failed to create user via Edge Function' }
      }

      if (role === "pharmacist") {
        const createdId = data?.data?.adminUserId || data?.data?.id || data?.user?.id || data?.id
        await this.updatePharmacistType(createdId, pharmacistType, { allowMissing: true })
      }

      return { success: true, data: data.data }
    } catch (error) {
      console.error('� Create user error:', error)
      return { success: false, error: error.message }
    }
  }

  // Update admin user profile (role/email/respondio/greeting) via Edge Function
  static async updateAdminUserProfile(
    adminUserId,
    { role, email, respondIoId = null, greetingMessage = null, pharmacistType = "full_time" }
  ) {
    try {
      if (!this.canManageUsers()) {
        return { success: false, error: "Unauthorized" };
      }

      const normalizedEmail = String(email || "").trim().toLowerCase();
      if (!adminUserId || !role || !normalizedEmail) {
        return { success: false, error: "Admin user ID, role, and email are required" };
      }

      const validRoles = ["super_admin", "pharmacist", "coordinator"];
      if (!validRoles.includes(role)) {
        return { success: false, error: "Invalid role" };
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(normalizedEmail)) {
        return { success: false, error: "Invalid email address" };
      }

      const { data, error } = await supabase.functions.invoke("update-admin-user-profile", {
        body: {
          adminUserId,
          role,
          email: normalizedEmail,
          respondIoId,
          greetingMessage,
          pharmacistType: role === "pharmacist" ? pharmacistType : null,
        },
      });

      if (error) {
        console.error("❌ update-admin-user-profile invoke error:", error);
        return {
          success: false,
          error: error.message || "Failed to update user profile via Edge Function",
        };
      }

      if (!data?.success) {
        return {
          success: false,
          error: data?.error || "Failed to update user profile via Edge Function",
        };
      }

      if (role === "pharmacist") {
        await this.updatePharmacistType(adminUserId, pharmacistType, { allowMissing: true });
      }

      return { success: true, data: data.data };
    } catch (error) {
      console.error("❌ Error in updateAdminUserProfile:", error);
      return { success: false, error: error.message };
    }
  }

  static async updatePharmacistType(adminUserId, pharmacistType = "full_time", { allowMissing = false } = {}) {
    try {
      const normalizedType = pharmacistType === "freelance" ? "freelance" : "full_time";
      if (!adminUserId) {
        if (allowMissing) return { success: true, skipped: true };
        return { success: false, error: "Admin user ID is required" };
      }

      const { error } = await supabase
        .from("round_robin")
        .update({ pharmacist_type: normalizedType })
        .eq("admin_user_id", adminUserId);

      if (error) {
        if (allowMissing) {
          console.warn("⚠️ Could not update pharmacist type yet:", error.message);
          return { success: true, warning: error.message };
        }
        return { success: false, error: error.message };
      }

      return { success: true, pharmacist_type: normalizedType };
    } catch (error) {
      if (allowMissing) {
        console.warn("⚠️ Could not update pharmacist type yet:", error.message);
        return { success: true, warning: error.message };
      }
      return { success: false, error: error.message };
    }
  }

  // Update admin user (activate/deactivate, change role)
  static async updateAdminUser(userId, updates) {
    try {
      if (!this.canManageUsers()) {
        return { success: false, error: "Unauthorized" };
      }

      const currentUser = this.getCurrentUser();

      // Prevent self-demotion or self-deactivation
      if (userId === currentUser?.id) {
        if (updates.is_active === false) {
          return {
            success: false,
            error: "Cannot deactivate your own account",
          };
        }
        if (updates.role && updates.role !== "super_admin") {
          return { success: false, error: "Cannot demote your own account" };
        }
      }

      // Validate role if provided
      if (updates.role) {
        const validRoles = ["super_admin", "pharmacist", "coordinator"];
        if (!validRoles.includes(updates.role)) {
          return { success: false, error: "Invalid role" };
        }
      }

      // Build update object
      const updateData = {};
      if (typeof updates.is_active === "boolean") {
        updateData.is_active = updates.is_active;
      }
      if (updates.role) {
        updateData.role = updates.role;
      }

      if (Object.keys(updateData).length === 0) {
        return { success: false, error: "No valid updates provided" };
      }

      const { data, error } = await supabase
        .from("admin_users")
        .update(updateData)
        .eq("id", userId)
        .select("id, username, role, is_active, created_at")
        .single();

      if (error) {
        console.error("❌ User update error:", error);
        return { success: false, error: error.message };
      }

      // If user is being deactivated, also set them offline in round_robin
      if (updates.is_active === false) {
        await supabase
          .from("round_robin")
          .update({ is_online: false, is_active: false })
          .eq("admin_user_id", userId);
      }

      // If user is being reactivated, set round_robin active but leave offline
      if (updates.is_active === true) {
        await supabase
          .from("round_robin")
          .update({ is_active: true })
          .eq("admin_user_id", userId);
      }

      console.log("✅ Admin user updated:", data.username);
      return { success: true, data };
    } catch (error) {
      console.error("❌ Error in updateAdminUser:", error);
      return { success: false, error: error.message };
    }
  }

  // Create round_robin entry for new user - DISABLED (requires Edge Function)
  static async createRoundRobinEntry(adminUserId) {
    console.warn(
      "⚠️ createRoundRobinEntry is disabled - use Supabase Dashboard"
    );
    return {
      success: false,
      error:
        "Round robin creation via UI is disabled. Please use Supabase Dashboard.",
    };
  }

  // Reset user password - DISABLED (requires Edge Function)
  // Use Supabase Dashboard: Authentication → Users → Find user → Reset password
  static async resetUserPassword(userId, newPassword) {
    console.warn("⚠️ resetUserPassword is disabled - use Supabase Dashboard");
    return {
      success: false,
      error:
        "Password reset via UI is disabled. Please use Supabase Dashboard to reset passwords.",
    };
  }

  // ============================================
  // DAILY REPORTS METHODS
  // ============================================

  static _canAccessDailyReports() {
    const currentUser = this.getCurrentUser();
    return (
      currentUser?.role === "super_admin" ||
      currentUser?.role === "coordinator"
    );
  }

  static _getRankingBusinessDayStartUtc(dateStr) {
    return new Date(`${dateStr}T03:00:00.000Z`).toISOString();
  }

  static _shiftRankingDate(dateStr, days) {
    const date = new Date(`${dateStr}T00:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  }

  static _normalizeRankingUsername(value) {
    return String(value || "").trim();
  }

  static _parseRankingTimestamp(value) {
    if (!value) return null;

    const trimmed = String(value).trim();
    if (!trimmed) return null;

    const withT = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
    const hasOffset = /([zZ]|[+-]\d{2}:\d{2}|[+-]\d{4}|[+-]\d{2})$/.test(withT);
    const candidate = hasOffset ? withT : `${withT}Z`;
    const date = new Date(candidate);

    if (Number.isNaN(date.getTime())) return null;
    return date;
  }

  static _isRankingTimestampInWindow(value, startIso, endIso) {
    const parsed = this._parseRankingTimestamp(value);
    if (!parsed) return false;

    const timestampMs = parsed.getTime();
    return (
      timestampMs >= new Date(startIso).getTime() &&
      timestampMs < new Date(endIso).getTime()
    );
  }

  static _getRankingBusinessDate(value) {
    const parsed = this._parseRankingTimestamp(value);
    if (!parsed) return null;

    const shifted = new Date(parsed.getTime());
    shifted.setUTCHours(shifted.getUTCHours() - 3);
    return shifted.toISOString().slice(0, 10);
  }

  static _compareRankingLogRowsDesc(a, b) {
    const aAssigned = this._parseRankingTimestamp(a.assigned_at)?.getTime() || 0;
    const bAssigned = this._parseRankingTimestamp(b.assigned_at)?.getTime() || 0;
    if (aAssigned !== bAssigned) return bAssigned - aAssigned;

    const aCreated = this._parseRankingTimestamp(a.created_at)?.getTime() || 0;
    const bCreated = this._parseRankingTimestamp(b.created_at)?.getTime() || 0;
    if (aCreated !== bCreated) return bCreated - aCreated;

    return String(b.id || "").localeCompare(String(a.id || ""));
  }

  static _pickRankingAttributionRow(rows, completedInWindow, windowStartUtc, windowEndUtc) {
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const withName = rows.filter(
      (row) => this._normalizeRankingUsername(row.pharmacist_username).length > 0
    );

    if (completedInWindow) {
      return withName[0] || rows[0] || null;
    }

    const assignedInWindow = withName.filter((row) =>
      this._isRankingTimestampInWindow(row.assigned_at, windowStartUtc, windowEndUtc)
    );

    return assignedInWindow[0] || null;
  }

  static async getDailyPharmacistRankingAudit(reportDate) {
    try {
      if (!this._canAccessDailyReports()) {
        return { success: false, error: "Unauthorized" };
      }

      if (!reportDate) {
        return { success: false, error: "reportDate is required" };
      }

      const windowStartUtc = this._getRankingBusinessDayStartUtc(reportDate);
      const windowEndUtc = this._getRankingBusinessDayStartUtc(
        this._shiftRankingDate(reportDate, 1)
      );

      const [completedCandidatesResult, fallbackAssignedResult] = await Promise.all([
        supabase
          .from("user_consultations")
          .select("id")
          .gte("completed_at", windowStartUtc)
          .lt("completed_at", windowEndUtc)
          .limit(100000),
        supabase
          .from("pharmacist_assignment_log")
          .select("consultation_id")
          .not("consultation_id", "is", null)
          .gte("assigned_at", windowStartUtc)
          .lt("assigned_at", windowEndUtc)
          .limit(100000),
      ]);

      if (completedCandidatesResult.error) {
        return { success: false, error: completedCandidatesResult.error.message };
      }

      if (fallbackAssignedResult.error) {
        return { success: false, error: fallbackAssignedResult.error.message };
      }

      const candidateIds = new Set();

      (completedCandidatesResult.data || []).forEach((row) => {
        const id = String(row?.id || "").trim();
        if (id) candidateIds.add(id);
      });

      (fallbackAssignedResult.data || []).forEach((row) => {
        const id = String(row?.consultation_id || "").trim();
        if (id) candidateIds.add(id);
      });

      const candidateIdList = Array.from(candidateIds);

      if (candidateIdList.length === 0) {
        return {
          success: true,
          data: {
            report_date: reportDate,
            timezone_name: "Asia/Damascus",
            window_start_utc: windowStartUtc,
            window_end_utc: windowEndUtc,
            total_consultations: 0,
            attributed_consultations: 0,
            unattributed_consultations: 0,
            rows: [],
            evidence_by_pharmacist: {},
          },
        };
      }

      const chunkArray = (values, size) => {
        const chunks = [];
        for (let i = 0; i < values.length; i += size) {
          chunks.push(values.slice(i, i + size));
        }
        return chunks;
      };

      const canonicalRows = [];
      const logRows = [];

      for (const chunk of chunkArray(candidateIdList, 500)) {
        const [canonicalResult, logResult] = await Promise.all([
          supabase
            .from("user_consultations")
            .select(
              "id, user_id, assigned_pharmacist, consultation_status, consultation_outcome, completed_at"
            )
            .in("id", chunk),
          supabase
            .from("pharmacist_assignment_log")
            .select(
              "id, consultation_id, pharmacist_username, assigned_at, handled_at, completed_at, created_at, assignment_type, previous_pharmacist, current_status, final_outcome"
            )
            .in("consultation_id", chunk)
            .order("assigned_at", { ascending: false, nullsFirst: false })
            .order("created_at", { ascending: false, nullsFirst: false }),
        ]);

        if (canonicalResult.error) {
          return { success: false, error: canonicalResult.error.message };
        }

        if (logResult.error) {
          return { success: false, error: logResult.error.message };
        }

        canonicalRows.push(...(canonicalResult.data || []));
        logRows.push(...(logResult.data || []));
      }

      const userIds = Array.from(
        new Set(
          canonicalRows
            .map((row) => String(row?.user_id || "").trim())
            .filter(Boolean)
        )
      );

      const usersById = new Map();

      for (const chunk of chunkArray(userIds, 500)) {
        const { data: users, error: usersError } = await supabase
          .from("users")
          .select("id, name, phone")
          .in("id", chunk);

        if (usersError) {
          return { success: false, error: usersError.message };
        }

        (users || []).forEach((user) => {
          usersById.set(user.id, user);
        });
      }

      const logsByConsultation = new Map();

      logRows.forEach((row) => {
        const consultationId = String(row?.consultation_id || "").trim();
        if (!consultationId) return;

        const bucket = logsByConsultation.get(consultationId) || [];
        bucket.push(row);
        logsByConsultation.set(consultationId, bucket);
      });

      logsByConsultation.forEach((bucket) => {
        bucket.sort((a, b) => this._compareRankingLogRowsDesc(a, b));
      });

      const statsByPharmacist = new Map();
      const evidenceByPharmacist = new Map();
      let totalConsultations = 0;
      let attributedConsultations = 0;
      let unattributedConsultations = 0;

      canonicalRows.forEach((canonical) => {
        const completedInWindow = this._isRankingTimestampInWindow(
          canonical.completed_at,
          windowStartUtc,
          windowEndUtc
        );
        const rows = logsByConsultation.get(canonical.id) || [];
        const attributionRow = this._pickRankingAttributionRow(
          rows,
          completedInWindow,
          windowStartUtc,
          windowEndUtc
        );
        const includeByFallback = !completedInWindow && attributionRow !== null;

        if (!completedInWindow && !includeByFallback) {
          return;
        }

        totalConsultations += 1;

        const pharmacistName =
          this._normalizeRankingUsername(attributionRow?.pharmacist_username) || "Unknown";

        if (pharmacistName === "Unknown") {
          unattributedConsultations += 1;
          return;
        }

        attributedConsultations += 1;

        const existingStats = statsByPharmacist.get(pharmacistName) || {
          pharmacist_name: pharmacistName,
          total_cases: 0,
          assigned_today: 0,
          completed_today: 0,
          same_day_completed: 0,
          completed_from_backlog: 0,
          assigned_today_only: 0,
          total_cases_paid: 0,
          hot_lead: 0,
          cold_lead: 0,
          completed: 0,
        };

        existingStats.total_cases += 1;

        const status = String(canonical.consultation_status || "").trim().toLowerCase();
        const outcome = String(canonical.consultation_outcome || "").trim().toLowerCase();
        const assignedInWindow = this._isRankingTimestampInWindow(
          attributionRow?.assigned_at,
          windowStartUtc,
          windowEndUtc
        );
        const timingBucket = completedInWindow
          ? assignedInWindow
            ? "assigned_and_completed_today"
            : "completed_today_from_backlog"
          : "assigned_today_only";

        if (assignedInWindow) existingStats.assigned_today += 1;
        if (completedInWindow) existingStats.completed_today += 1;
        if (timingBucket === "assigned_and_completed_today") existingStats.same_day_completed += 1;
        if (timingBucket === "completed_today_from_backlog") existingStats.completed_from_backlog += 1;
        if (timingBucket === "assigned_today_only") existingStats.assigned_today_only += 1;

        if (status === "completed") existingStats.completed += 1;
        if (outcome === "paid") existingStats.total_cases_paid += 1;
        if (outcome === "hot_lead") existingStats.hot_lead += 1;
        if (outcome === "cold_lead") existingStats.cold_lead += 1;

        statsByPharmacist.set(pharmacistName, existingStats);

        const user = usersById.get(canonical.user_id) || null;
        const evidenceRows = evidenceByPharmacist.get(pharmacistName) || [];

        evidenceRows.push({
          consultation_id: canonical.id,
          user_id: canonical.user_id,
          user_name: user?.name || null,
          user_phone: user?.phone || null,
          inclusion_basis: completedInWindow ? "completed_at" : "assigned_at_fallback",
          timing_bucket: timingBucket,
          assigned_in_window: assignedInWindow,
          completed_in_window: completedInWindow,
          assigned_business_date: this._getRankingBusinessDate(attributionRow?.assigned_at),
          completed_business_date: this._getRankingBusinessDate(canonical.completed_at),
          consultation_status: canonical.consultation_status || null,
          consultation_outcome: canonical.consultation_outcome || null,
          canonical_completed_at: canonical.completed_at || null,
          canonical_assigned_pharmacist: canonical.assigned_pharmacist || null,
          attributed_log_id: attributionRow?.id || null,
          attributed_assigned_at: attributionRow?.assigned_at || null,
          attributed_handled_at: attributionRow?.handled_at || null,
          attributed_completed_at: attributionRow?.completed_at || null,
          attributed_assignment_type: attributionRow?.assignment_type || null,
          attributed_previous_pharmacist: attributionRow?.previous_pharmacist || null,
          attributed_log_status: attributionRow?.current_status || null,
          attributed_log_outcome: attributionRow?.final_outcome || null,
        });

        evidenceByPharmacist.set(pharmacistName, evidenceRows);
      });

      const rows = Array.from(statsByPharmacist.values())
        .sort((a, b) => {
          if (b.completed !== a.completed) return b.completed - a.completed;
          if (b.total_cases !== a.total_cases) return b.total_cases - a.total_cases;
          return a.pharmacist_name.localeCompare(b.pharmacist_name, "en", {
            sensitivity: "base",
          });
        })
        .map((row, index) => ({
          ...row,
          rank: index + 1,
          evidence_rows: (evidenceByPharmacist.get(row.pharmacist_name) || []).sort((a, b) => {
            const aCompleted = this._parseRankingTimestamp(a.canonical_completed_at)?.getTime() || 0;
            const bCompleted = this._parseRankingTimestamp(b.canonical_completed_at)?.getTime() || 0;
            if (aCompleted !== bCompleted) return bCompleted - aCompleted;

            const aAssigned = this._parseRankingTimestamp(a.attributed_assigned_at)?.getTime() || 0;
            const bAssigned = this._parseRankingTimestamp(b.attributed_assigned_at)?.getTime() || 0;
            if (aAssigned !== bAssigned) return bAssigned - aAssigned;

            return String(a.consultation_id || "").localeCompare(String(b.consultation_id || ""));
          }),
        }));

      const serializedEvidenceByPharmacist = {};
      evidenceByPharmacist.forEach((value, key) => {
        serializedEvidenceByPharmacist[key] = value;
      });

      return {
        success: true,
        data: {
          report_date: reportDate,
          timezone_name: "Asia/Damascus",
          window_start_utc: windowStartUtc,
          window_end_utc: windowEndUtc,
          total_consultations: totalConsultations,
          attributed_consultations: attributedConsultations,
          unattributed_consultations: unattributedConsultations,
          rows,
          evidence_by_pharmacist: serializedEvidenceByPharmacist,
        },
      };
    } catch (error) {
      console.error("❌ Error in getDailyPharmacistRankingAudit:", error);
      return { success: false, error: error.message };
    }
  }

  // Get daily consultation reports for the specified date range
  // Uses pharmacist_assignment_log for accurate tracking with backlog breakdown
  static async getDailyConsultationReports(startDate, endDate) {
    try {
      if (!this._canAccessDailyReports()) {
        return { success: false, error: "Unauthorized" };
      }

      console.log("📊 Fetching daily reports from", startDate, "to", endDate);

      // For matching daily_counter, we use 3 AM UTC as the day boundary
      const getDayStartUTC = (dateStr) => {
        const d = new Date(dateStr + "T03:00:00.000Z");
        return d.toISOString();
      };

      const getDayEndUTC = (dateStr) => {
        const d = new Date(dateStr + "T03:00:00.000Z");
        d.setUTCDate(d.getUTCDate() + 1);
        return d.toISOString();
      };

      // Query from 3 AM on the day BEFORE startDate
      const startDateObj = new Date(startDate);
      startDateObj.setDate(startDateObj.getDate() - 1);
      const queryStartDate = startDateObj.toISOString().split('T')[0];

      const queryStartUTC = getDayStartUTC(queryStartDate);
      const queryEndUTC = getDayEndUTC(endDate);

      // Get list of ALL pharmacists
      const { data: pharmacists } = await supabase
        .from("admin_users")
        .select("username")
        .eq("role", "pharmacist");

      const pharmacistList = pharmacists?.map((p) => p.username) || [];

      // ============================================
      // NEW: Query from pharmacist_assignment_log
      // This gives us accurate handled counts with backlog info
      // ============================================

      // Query handled consultations in date range
      const { data: handledData, error: handledError } = await supabase
        .from("pharmacist_assignment_log")
        .select("*")
        .gte("handled_at", queryStartUTC)
        .lte("handled_at", queryEndUTC)
        .not("handled_at", "is", null)
        .order("handled_at", { ascending: false });

      if (handledError) {
        console.error("❌ Error fetching from assignment log:", handledError);
        // Fallback to old method if log table not available
        return this._getDailyConsultationReportsLegacy(startDate, endDate);
      }

      // Query open wondura_done consultations in date range
      // (handled but not completed) for the Wondura Handled metric.
      const { data: openWonduraData, error: openWonduraError } = await supabase
        .from("pharmacist_assignment_log")
        .select("*")
        .gte("handled_at", queryStartUTC)
        .lte("handled_at", queryEndUTC)
        .not("handled_at", "is", null)
        .is("completed_at", null)
        .is("final_outcome", null)
        .eq("current_status", "wondura_done")
        .order("handled_at", { ascending: false });

      if (openWonduraError) {
        console.error("❌ Error fetching open wondura handled records:", openWonduraError);
      }

      // Query assignments (for pending count and reassignments)
      const { data: assignedData, error: assignedError } = await supabase
        .from("pharmacist_assignment_log")
        .select("*")
        .gte("assigned_at", queryStartUTC)
        .lte("assigned_at", queryEndUTC)
        .order("assigned_at", { ascending: false });

      if (assignedError) {
        console.error("❌ Error fetching assignments:", assignedError);
      }

      // Query pending consultations (not yet handled)
      const { data: pendingData, error: pendingError } = await supabase
        .from("pharmacist_assignment_log")
        .select("*")
        .is("handled_at", null)
        .not("current_status", "eq", "completed");

      if (pendingError) {
        console.error("❌ Error fetching pending:", pendingError);
      }

      console.log(`📊 Fetched: ${handledData?.length || 0} handled, ${openWonduraData?.length || 0} open wondura, ${assignedData?.length || 0} assigned, ${pendingData?.length || 0} pending`);

      // Generate date range array
      const dates = [];
      const start = new Date(startDate);
      const end = new Date(endDate);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d).toISOString().split("T")[0]);
      }

      // Helper to get report day (3 AM UTC boundary)
      const getReportDay = (utcStr) => {
        if (!utcStr) return null;
        const dateStr = utcStr.endsWith("Z") || utcStr.includes("+") ? utcStr : utcStr + "Z";
        const d = new Date(dateStr);
        d.setUTCHours(d.getUTCHours() - 3);
        return d.toISOString().split('T')[0];
      };

      const DAILY_REPORT_STATUS_KEYS = ["pending", "wondura_done", "completed", "unknown"];
      const DAILY_REPORT_OUTCOME_KEYS = ["paid", "hot_lead", "cold_lead", "none"];
      const createCountsMap = (keys) =>
        keys.reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {});
      const incrementCount = (countsMap, key) => {
        if (!countsMap || !key) return;
        countsMap[key] = (countsMap[key] || 0) + 1;
      };
      const normalizeStatusKey = (status) => {
        if (typeof status !== "string" || !status.trim()) return "unknown";
        return status.trim().toLowerCase();
      };
      const normalizeOutcomeKey = (outcome) => {
        if (typeof outcome !== "string" || !outcome.trim()) return "none";
        return outcome.trim().toLowerCase();
      };

      // Initialize report structure
      const reportData = {};
      dates.forEach((date) => {
        reportData[date] = {
          date,
          pharmacists: {},
          totals: {
            round_robin_assigned: 0,
            assigned_processed: 0,
            wondura_handled: 0,
            pending: 0,
            paid: 0,
            reassignments_received: 0,
            same_day_count: 0,
            week_backlog_count: 0,
            two_week_backlog_count: 0,
            old_backlog_count: 0,
            status_counts: createCountsMap(DAILY_REPORT_STATUS_KEYS),
            outcome_counts: createCountsMap(DAILY_REPORT_OUTCOME_KEYS),
          },
        };
        pharmacistList.forEach((username) => {
          reportData[date].pharmacists[username] = {
            round_robin_assigned: 0,
            assigned_processed: 0,
            wondura_handled: 0,
            pending: 0,
            paid: 0,
            reassignments_received: 0,
            same_day_count: 0,
            week_backlog_count: 0,
            two_week_backlog_count: 0,
            old_backlog_count: 0,
            status_counts: createCountsMap(DAILY_REPORT_STATUS_KEYS),
            outcome_counts: createCountsMap(DAILY_REPORT_OUTCOME_KEYS),
            _rr_incoming: 0,
            _rr_outgoing: 0,
            backlog_details: [], // NEW: Individual consultation details
          };
        });
      });

      // Process HANDLED consultations (the main metric)
      handledData?.forEach((record) => {
        const pharmacist = record.pharmacist_username;
        if (!pharmacist || !pharmacistList.includes(pharmacist)) return;

        const handledDate = getReportDay(record.handled_at);
        if (!handledDate || !reportData[handledDate]) return;

        const stats = reportData[handledDate].pharmacists[pharmacist];
        const totals = reportData[handledDate].totals;

        // Count as assigned & processed
        stats.assigned_processed++;
        totals.assigned_processed++;

        // Backlog breakdown
        const backlogDays = record.backlog_days || 0;
        if (backlogDays === 0) {
          stats.same_day_count++;
          totals.same_day_count++;
        } else if (backlogDays <= 7) {
          stats.week_backlog_count++;
          totals.week_backlog_count++;
        } else if (backlogDays <= 14) {
          stats.two_week_backlog_count++;
          totals.two_week_backlog_count++;
        } else {
          stats.old_backlog_count++;
          totals.old_backlog_count++;
        }

        // Add backlog detail for UI
        stats.backlog_details.push({
          consultation_id: record.consultation_id,
          created_at: record.consultation_created_at,
          handled_at: record.handled_at,
          backlog_days: backlogDays,
          assignment_type: record.assignment_type,
          previous_pharmacist: record.previous_pharmacist,
        });

        // Paid count
        if (record.final_outcome === "paid") {
          stats.paid++;
          totals.paid++;
        }
      });

      // Process WONDURA HANDLED (open only: wondura_done and not completed)
      openWonduraData?.forEach((record) => {
        const pharmacist = record.pharmacist_username;
        if (!pharmacist || !pharmacistList.includes(pharmacist)) return;

        const handledDate = getReportDay(record.handled_at);
        if (!handledDate || !reportData[handledDate]) return;

        const stats = reportData[handledDate].pharmacists[pharmacist];
        const totals = reportData[handledDate].totals;

        stats.wondura_handled++;
        totals.wondura_handled++;
      });

      // Process assignment flow events for:
      // 1) reassignments received
      // 2) net round-robin assigned load (in - out) by business day
      assignedData?.forEach((record) => {
        const assignedDate = getReportDay(record.assigned_at);
        if (!assignedDate || !reportData[assignedDate]) return;

        const pharmacist = record.pharmacist_username;
        const previousPharmacist = record.previous_pharmacist;
        const dayBucket = reportData[assignedDate];

        if (pharmacist && pharmacistList.includes(pharmacist) && dayBucket.pharmacists[pharmacist]) {
          dayBucket.pharmacists[pharmacist]._rr_incoming++;

          const statusKey = normalizeStatusKey(record.current_status);
          const outcomeKey = normalizeOutcomeKey(record.final_outcome);
          incrementCount(dayBucket.pharmacists[pharmacist].status_counts, statusKey);
          incrementCount(dayBucket.totals.status_counts, statusKey);
          incrementCount(dayBucket.pharmacists[pharmacist].outcome_counts, outcomeKey);
          incrementCount(dayBucket.totals.outcome_counts, outcomeKey);
        }

        if (
          previousPharmacist &&
          previousPharmacist !== pharmacist &&
          pharmacistList.includes(previousPharmacist) &&
          dayBucket.pharmacists[previousPharmacist]
        ) {
          dayBucket.pharmacists[previousPharmacist]._rr_outgoing++;
        }

        // Count reassignments
        if (
          pharmacist &&
          pharmacistList.includes(pharmacist) &&
          (record.assignment_type === "manual" || record.assignment_type === "reassignment")
        ) {
          dayBucket.pharmacists[pharmacist].reassignments_received++;
          dayBucket.totals.reassignments_received++;
        }
      });

      // Process PENDING consultations (current workload)
      pendingData?.forEach((record) => {
        const pharmacist = record.pharmacist_username;
        if (!pharmacist || !pharmacistList.includes(pharmacist)) return;

        // Bucket pending once by its assignment day (3 AM UTC business-day boundary).
        const pendingDate = getReportDay(record.assigned_at || record.consultation_created_at);
        if (!pendingDate || !reportData[pendingDate]) return;

        if (reportData[pendingDate].pharmacists[pharmacist]) {
          reportData[pendingDate].pharmacists[pharmacist].pending++;
          reportData[pendingDate].totals.pending++;
        }
      });

      // Finalize round-robin assigned load metric per day/pharmacist
      dates.forEach((date) => {
        const dayBucket = reportData[date];
        pharmacistList.forEach((username) => {
          const stats = dayBucket.pharmacists[username];
          const roundRobinAssigned = Math.max(
            (stats._rr_incoming || 0) - (stats._rr_outgoing || 0),
            0
          );
          stats.round_robin_assigned = roundRobinAssigned;
          dayBucket.totals.round_robin_assigned += roundRobinAssigned;

          delete stats._rr_incoming;
          delete stats._rr_outgoing;
        });
      });

      // Convert to array sorted by date descending
      const reportArray = Object.values(reportData).sort((a, b) =>
        b.date.localeCompare(a.date)
      );

      console.log(`✅ Daily reports generated: ${reportArray.length} days with backlog breakdown`);
      return { success: true, data: reportArray, pharmacistList };
    } catch (error) {
      console.error("❌ Error in getDailyConsultationReports:", error);
      return { success: false, error: error.message };
    }
  }

  // Legacy method (fallback if log table not available)
  static async _getDailyConsultationReportsLegacy(startDate, endDate) {
    console.warn("⚠️ Using legacy report method - log table may not be available");
    // ... existing implementation from before ...
    return { success: false, error: "Assignment log not available" };
  }

  // ============================================
  // PENDING CONSULTATIONS METHODS
  // ============================================

  // Get pending status for multiple users (for ConsultationsTable badge)
  static async getPendingStatusForUsers(userIds) {
    try {
      if (!userIds || userIds.length === 0) {
        return { success: true, data: {} };
      }

      const { data, error } = await supabase
        .from("pending_consultations")
        .select(
          "user_id, status, queued_at, dispatched_at, assigned_pharmacist_id"
        )
        .in("user_id", userIds);

      if (error) {
        console.error("❌ Error fetching pending status:", error);
        return { success: false, error: error.message };
      }

      // Create a map of user_id -> pending record
      const pendingMap = {};
      if (data) {
        data.forEach((record) => {
          pendingMap[record.user_id] = record;
        });
      }

      return { success: true, data: pendingMap };
    } catch (error) {
      console.error("❌ Error in getPendingStatusForUsers:", error);
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // GHOST DISPATCH LOG (super-admin investigation)
  // ============================================

  // Read entries from the persistent ghost_dispatch_log. Detection runs server-side
  // every 5 minutes via pg_cron (sql/ghost_dispatch_log.sql). This method just reads.
  static async getGhostDispatchLog({
    status = "unresolved", // 'unresolved' | 'resolved' | 'all'
    kind = null, // 'ghost_dispatched' | 'ready_unqueued' | null
    dispatcherSource = null,
    limit = 100,
    offset = 0,
  } = {}) {
    try {
      const currentUser = this.getCurrentUser();
      if (currentUser?.role !== "super_admin") {
        return { success: false, error: "Unauthorized" };
      }

      let query = supabase
        .from("ghost_dispatch_log")
        .select(
          `
          id,
          kind,
          pending_id,
          user_id,
          consultation_id,
          dispatched_at,
          assigned_pharmacist_id,
          expected_pharmacist_username,
          consultation_type,
          consultation_status,
          dispatcher_source,
          n8n_execution_id,
          first_detected_at,
          last_detected_at,
          detection_count,
          resolved_at,
          resolved_by,
          resolution,
          resolution_notes,
          users:user_id (name, phone),
          resolver:resolved_by (username)
        `,
          { count: "exact" }
        )
        .order("first_detected_at", { ascending: false })
        .range(offset, offset + limit - 1);

      if (status === "unresolved") query = query.is("resolved_at", null);
      else if (status === "resolved") query = query.not("resolved_at", "is", null);
      if (kind) query = query.eq("kind", kind);
      if (dispatcherSource) query = query.eq("dispatcher_source", dispatcherSource);

      const { data, error, count } = await query;
      if (error) {
        console.error("❌ Error fetching ghost dispatch log:", error);
        return { success: false, error: error.message };
      }

      const entries = (data || []).map((row) => ({
        log_id: row.id,
        kind: row.kind,
        pending_id: row.pending_id,
        user_id: row.user_id,
        user_name: row.users?.name || null,
        user_phone: row.users?.phone || null,
        consultation_id: row.consultation_id,
        consultation_type: row.consultation_type,
        consultation_status: row.consultation_status,
        dispatched_at: row.dispatched_at,
        assigned_pharmacist_id: row.assigned_pharmacist_id,
        expected_pharmacist_username: row.expected_pharmacist_username,
        dispatcher_source: row.dispatcher_source,
        n8n_execution_id: row.n8n_execution_id,
        first_detected_at: row.first_detected_at,
        last_detected_at: row.last_detected_at,
        detection_count: row.detection_count,
        resolved_at: row.resolved_at,
        resolved_by: row.resolved_by,
        resolved_by_username: row.resolver?.username || null,
        resolution: row.resolution,
        resolution_notes: row.resolution_notes,
      }));

      return { success: true, entries, total: count ?? entries.length };
    } catch (error) {
      console.error("❌ Error in getGhostDispatchLog:", error);
      return { success: false, error: error.message };
    }
  }

  // Quick stats for the tab badge: counts of unresolved entries by kind.
  static async getGhostDispatchLogStats() {
    try {
      const currentUser = this.getCurrentUser();
      if (currentUser?.role !== "super_admin") {
        return { success: false, error: "Unauthorized" };
      }
      const { data, error } = await supabase
        .from("ghost_dispatch_log")
        .select("kind")
        .is("resolved_at", null);
      if (error) {
        console.error("❌ Error fetching ghost dispatch stats:", error);
        return { success: false, error: error.message };
      }
      const stats = { ghost_dispatched: 0, ready_unqueued: 0, total: 0 };
      (data || []).forEach((row) => {
        if (stats[row.kind] !== undefined) stats[row.kind] += 1;
        stats.total += 1;
      });
      return { success: true, stats };
    } catch (error) {
      console.error("❌ Error in getGhostDispatchLogStats:", error);
      return { success: false, error: error.message };
    }
  }

  // Mark a log entry as resolved with a chosen resolution + optional notes.
  static async markGhostResolved({ logId, resolution, notes = null }) {
    try {
      const currentUser = this.getCurrentUser();
      if (currentUser?.role !== "super_admin") {
        return { success: false, error: "Unauthorized" };
      }
      const valid = ["backfilled", "manual_fix", "false_positive", "ignored"];
      if (!logId || !valid.includes(resolution)) {
        return { success: false, error: "Invalid logId or resolution" };
      }
      const { error } = await supabase
        .from("ghost_dispatch_log")
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: currentUser.id,
          resolution,
          resolution_notes: notes,
        })
        .eq("id", logId)
        .is("resolved_at", null);
      if (error) {
        console.error("❌ Error marking ghost resolved:", error);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (error) {
      console.error("❌ Error in markGhostResolved:", error);
      return { success: false, error: error.message };
    }
  }

  // Backfill user_consultations.assigned_pharmacist with a username, then auto-resolve
  // the corresponding log entry. Used by both ghost_dispatched and ready_unqueued cases.
  static async backfillGhostAssignedPharmacist({
    consultationId,
    expectedUsername,
    logId = null,
    notes = null,
  }) {
    try {
      const currentUser = this.getCurrentUser();
      if (currentUser?.role !== "super_admin") {
        return { success: false, error: "Unauthorized" };
      }
      if (!consultationId || !expectedUsername) {
        return { success: false, error: "Missing consultationId or expectedUsername" };
      }
      const { error } = await supabase
        .from("user_consultations")
        .update({ assigned_pharmacist: expectedUsername })
        .eq("id", consultationId)
        .is("assigned_pharmacist", null);
      if (error) {
        console.error("❌ Error backfilling assigned_pharmacist:", error);
        return { success: false, error: error.message };
      }
      if (logId) {
        await this.markGhostResolved({
          logId,
          resolution: "backfilled",
          notes,
        });
      }
      return { success: true };
    } catch (error) {
      console.error("❌ Error in backfillGhostAssignedPharmacist:", error);
      return { success: false, error: error.message };
    }
  }

  // Manually trigger the detection function — useful when admin wants to scan now
  // instead of waiting for the next 5-min cron tick.
  static async triggerGhostDetection() {
    try {
      const currentUser = this.getCurrentUser();
      if (currentUser?.role !== "super_admin") {
        return { success: false, error: "Unauthorized" };
      }
      const { data, error } = await supabase.rpc("detect_and_log_ghost_dispatches");
      if (error) {
        console.error("❌ Error triggering ghost detection:", error);
        return { success: false, error: error.message };
      }
      return { success: true, summary: data || [] };
    } catch (error) {
      console.error("❌ Error in triggerGhostDetection:", error);
      return { success: false, error: error.message };
    }
  }

  // Get all pending consultations with filtering and pagination
  static async getPendingConsultations({
    filter = "all",
    page = 1,
    pageSize = 20,
  } = {}) {
    try {
      const currentUser = this.getCurrentUser();
      const canAccess =
        currentUser?.role === "super_admin" ||
        currentUser?.role === "coordinator";

      if (!canAccess) {
        return { success: false, error: "Unauthorized" };
      }

      // Build query - join with user_consultations to get assigned_pharmacist (varchar)
      let query = supabase
        .from("pending_consultations")
        .select(
          `
          id,
          user_id,
          queued_at,
          status,
          dispatched_at,
          assigned_pharmacist_id,
          users!inner (
            id,
            name,
            phone,
            session_id,
            user_consultations (
              assigned_pharmacist
            )
          )
        `,
          { count: "exact" }
        )
        .order("queued_at", { ascending: false });


      // Apply filter
      if (filter === "queued") {
        query = query.eq("status", "queued");
      } else if (filter === "dispatched") {
        query = query.eq("status", "dispatched");
      }
      // 'all' filter doesn't add any condition

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error("❌ Error fetching pending consultations:", error);
        return { success: false, error: error.message };
      }

      // Get pharmacist names for dispatched consultations (only if we need to look up by ID)
      // First check if assigned_pharmacist (varchar) is available - use it directly
      // Fall back to assigned_pharmacist_id lookup if not
      const pharmacistIds = [
        ...new Set(
          data
            .filter((item) => item.assigned_pharmacist_id && !item.assigned_pharmacist)
            .map((item) => item.assigned_pharmacist_id)
        ),
      ];

      let pharmacistMap = {};
      if (pharmacistIds.length > 0) {
        const { data: pharmacists } = await supabase
          .from("admin_users")
          .select("id, username")
          .in("id", pharmacistIds);

        if (pharmacists) {
          pharmacists.forEach((p) => {
            pharmacistMap[p.id] = p.username;
          });
        }
      }

      // Transform data
      const transformedData = data.map((item) => {
        // Get assigned_pharmacist from nested user_consultations (could be array or object)
        const userConsultation = Array.isArray(item.users?.user_consultations)
          ? item.users.user_consultations[0]
          : item.users?.user_consultations;
        const assignedPharmacist = userConsultation?.assigned_pharmacist || null;

        return {
          id: item.id,
          user_id: item.user_id,
          user_name: item.users?.name || "Unknown",
          user_phone: item.users?.phone || "N/A",
          session_id: item.users?.session_id || null,
          queued_at: item.queued_at,
          status: item.status,
          dispatched_at: item.dispatched_at,
          assigned_pharmacist_id: item.assigned_pharmacist_id,
          // Use assigned_pharmacist from user_consultations, else fall back to ID lookup
          assigned_pharmacist_name:
            assignedPharmacist || pharmacistMap[item.assigned_pharmacist_id] || null,
          // Calculate wait time
          wait_time:
            item.dispatched_at && item.queued_at
              ? Math.round(
                (new Date(item.dispatched_at) - new Date(item.queued_at)) /
                60000
              )
              : null,
        };
      });

      // Get separate count for "Currently in Queue" stat card
      const { count: queuedTotal } = await supabase
        .from("pending_consultations")
        .select("*", { count: "exact", head: true })
        .eq("status", "queued");

      console.log(
        `✅ Pending consultations fetched: ${transformedData.length} of ${count} (Queued: ${queuedTotal})`
      );
      return {
        success: true,
        data: transformedData,
        total: count,
        queuedTotal: queuedTotal || 0,
        page,
        pageSize,
        totalPages: Math.ceil(count / pageSize),
      };
    } catch (error) {
      console.error("❌ Error in getPendingConsultations:", error);
      return { success: false, error: error.message };
    }
  }

  // ============================================
  // PHARMACIST ASSIGNMENT LOGS
  // ============================================

  // Get pharmacist assignment logs with filtering and pagination
  static async getPharmacistAssignmentLogs({
    filters = {},
    ownershipScope = "all",
    page = 1,
    pageSize = 20,
  } = {}) {
    try {
      const currentUser = this.getCurrentUser();
      const canAccess =
        currentUser?.role === "super_admin" ||
        currentUser?.role === "coordinator";

      if (!canAccess) {
        return { success: false, error: "Unauthorized" };
      }

      console.log("📊 Fetching pharmacist assignment logs with filters:", filters);

      // Build query
      const search = (filters.userSearch || "").trim();
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const isTextSearch = search && !uuidRegex.test(search);

      // If text searching, use strict inner join to correctly count and paginate matched users
      const selectStr = isTextSearch
        ? `
          *,
          users!inner (
            id,
            name,
            phone
          ),
          user_consultations (
            assignment_source
          )
        `
        : `
          *,
          users (
            id,
            name,
            phone
          ),
          user_consultations (
            assignment_source
          )
        `;

      const dateField = filters.dateField || "assigned_at";
      const batchSize = 1000;
      const maxBatches = 25;
      let allRows = [];
      let totalRawRows = 0;
      const currentConsultationCountPromise = this._getPharmacistCurrentConsultationCount({
        filters,
      });

      // Fetch all matching log rows in batches, then reduce to latest row per consultation.
      for (let batchIndex = 0; batchIndex < maxBatches; batchIndex++) {
        let batchQuery = supabase
          .from("pharmacist_assignment_log")
          .select(selectStr, { count: "exact" });

        if (filters.pharmacistUsername && filters.pharmacistUsername.trim() !== "all") {
          batchQuery = batchQuery.eq("pharmacist_username", filters.pharmacistUsername.trim());
        }

        if (filters.assignmentType && filters.assignmentType.trim() !== "all") {
          batchQuery = batchQuery.eq("assignment_type", filters.assignmentType.trim());
        }

        // Apply user search filter (name, phone, user_id, consultation_id)
        if (search) {
          if (isTextSearch) {
            batchQuery = batchQuery.or(`name.ilike.%${search}%,phone.ilike.%${search}%`, {
              foreignTable: "users",
            });
          } else {
            batchQuery = batchQuery.or(`user_id.eq.${search},consultation_id.eq.${search}`);
          }
        }

        if (filters.dateRange?.from) {
          batchQuery = batchQuery.gte(dateField, filters.dateRange.from);
        }
        if (filters.dateRange?.to) {
          batchQuery = batchQuery.lte(dateField, filters.dateRange.to);
        }

        batchQuery = batchQuery
          .order("assigned_at", { ascending: false })
          .order("created_at", { ascending: false })
          .range(batchIndex * batchSize, (batchIndex + 1) * batchSize - 1);

        const { data: batchRows, error: batchError, count } = await batchQuery;

        if (batchError) {
          console.error("❌ Error fetching pharmacist assignment logs:", batchError);
          return { success: false, error: batchError.message };
        }

        if (batchIndex === 0) {
          totalRawRows = count || 0;
        }

        if (!batchRows || batchRows.length === 0) {
          break;
        }

        allRows.push(...batchRows);

        if (batchRows.length < batchSize) {
          break;
        }

        if (totalRawRows > 0 && allRows.length >= totalRawRows) {
          break;
        }
      }

      const latestByConsultation = new Map();
      allRows.forEach((row) => {
        const key = row.consultation_id || `log:${row.id}`;
        if (!latestByConsultation.has(key)) {
          latestByConsultation.set(key, row);
        }
      });

      let mergedRows = Array.from(latestByConsultation.values());

      const pharmacistUsernames = [...new Set(
        mergedRows
          .map((row) => row.pharmacist_username || row.current_assigned_pharmacist)
          .filter(Boolean)
      )];
      const pharmacistTypeByUsername = new Map();
      if (pharmacistUsernames.length > 0) {
        const { data: rrRows, error: rrError } = await supabase
          .from("admin_users")
          .select("username, round_robin(pharmacist_type)")
          .in("username", pharmacistUsernames);

        if (!rrError) {
          (rrRows || []).forEach((row) => {
            const roundRobin = Array.isArray(row.round_robin) ? row.round_robin[0] : row.round_robin;
            const type = roundRobin?.pharmacist_type;
            pharmacistTypeByUsername.set(row.username, ["freelance", "full_time"].includes(type) ? type : null);
          });
        } else {
          console.warn("⚠️ Could not load pharmacist types for logs:", rrError.message);
          return { success: false, error: "Could not load pharmacist types. Please try again." };
        }
      }

      mergedRows = mergedRows.map((row) => ({
        ...row,
        pharmacist_type: pharmacistTypeByUsername.get(row.pharmacist_username || row.current_assigned_pharmacist) || null,
      }));

      if (filters.pharmacistType && filters.pharmacistType !== "all") {
        mergedRows = mergedRows.filter(
          (row) => row.pharmacist_type === filters.pharmacistType
        );
      }

      const consultationIds = mergedRows
        .map((row) => row.consultation_id)
        .filter((id) => Boolean(id));

      const canonicalByConsultationId = new Map();
      if (consultationIds.length > 0) {
        const chunkSize = 500;
        for (let i = 0; i < consultationIds.length; i += chunkSize) {
          const chunk = consultationIds.slice(i, i + chunkSize);
          const { data: consultations, error: consultError } = await supabase
            .from("user_consultations")
            .select(
              "id, consultation_status, consultation_outcome, assigned_pharmacist, completed_at, updated_at"
            )
            .in("id", chunk);

          if (consultError) {
            console.error("❌ Error fetching canonical consultation status/outcome:", consultError);
            return { success: false, error: consultError.message };
          }

          (consultations || []).forEach((consultation) => {
            canonicalByConsultationId.set(consultation.id, consultation);
          });
        }
      }

      mergedRows = mergedRows.map((row) => {
        const canonical = canonicalByConsultationId.get(row.consultation_id);
        if (!canonical) {
          return row;
        }
        return {
          ...row,
          current_status: canonical.consultation_status ?? row.current_status,
          final_outcome: canonical.consultation_outcome ?? row.final_outcome,
          current_assigned_pharmacist:
            canonical.assigned_pharmacist ?? row.pharmacist_username,
          completed_at: canonical.completed_at ?? row.completed_at,
          canonical_status_source: "user_consultations",
          canonical_updated_at: canonical.updated_at || null,
        };
      });

      if (filters.status && filters.status.trim() !== "all") {
        mergedRows = mergedRows.filter(
          (row) => row.current_status === filters.status.trim()
        );
      }

      if (filters.outcome && filters.outcome.trim() !== "all") {
        mergedRows = mergedRows.filter(
          (row) => row.final_outcome === filters.outcome.trim()
        );
      }

      const selectedPharmacist =
        filters.pharmacistUsername && filters.pharmacistUsername.trim() !== "all"
          ? filters.pharmacistUsername.trim()
          : null;
      if (selectedPharmacist && ownershipScope !== "all") {
        const normalizedSelected = selectedPharmacist.toLowerCase();
        mergedRows = mergedRows.filter((row) => {
          const currentOwner = (row.current_assigned_pharmacist || "").trim().toLowerCase();

          if (!currentOwner) {
            return false;
          }

          if (ownershipScope === "still_with_selected") {
            return currentOwner === normalizedSelected;
          }

          if (ownershipScope === "moved_away") {
            return currentOwner !== normalizedSelected;
          }

          return true;
        });
      }

      const orderBy = filters.orderBy || "assigned_at";
      const ascending = filters.ascending === true;
      const compareRows = (a, b) => {
        const av = a?.[orderBy];
        const bv = b?.[orderBy];

        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;

        const ad = Date.parse(av);
        const bd = Date.parse(bv);
        if (!Number.isNaN(ad) && !Number.isNaN(bd)) {
          return ad - bd;
        }

        if (typeof av === "number" && typeof bv === "number") {
          return av - bv;
        }

        return String(av).localeCompare(String(bv));
      };

      mergedRows.sort((a, b) => (ascending ? compareRows(a, b) : compareRows(b, a)));

      const total = mergedRows.length;
      const from = Math.max((page - 1) * pageSize, 0);
      const to = from + pageSize;
      const pagedRows = mergedRows.slice(from, to);
      const currentConsultationTotal = await currentConsultationCountPromise;

      const assignmentComparison = (() => {
        if (!selectedPharmacist) {
          return null;
        }

        const normalizedSelected = selectedPharmacist.toLowerCase();
        let stillWithSelectedCount = 0;
        let movedAwayCount = 0;
        let unknownCurrentOwnerCount = 0;

        mergedRows.forEach((row) => {
          const currentOwner = (row.current_assigned_pharmacist || "").trim();
          if (!currentOwner) {
            unknownCurrentOwnerCount += 1;
            return;
          }

          if (currentOwner.toLowerCase() === normalizedSelected) {
            stillWithSelectedCount += 1;
            return;
          }

          movedAwayCount += 1;
        });

        return {
          selectedPharmacist,
          totalCompared: total,
          stillWithSelectedCount,
          movedAwayCount,
          unknownCurrentOwnerCount,
        };
      })();

      console.log(
        `✅ Pharmacist assignment logs fetched: ${pagedRows.length} of ${total} (raw rows scanned: ${allRows.length}/${totalRawRows})`
      );

      return {
        success: true,
        data: pagedRows,
        total,
        currentConsultationTotal,
        assignmentComparison,
        page,
        pageSize,
        totalPages: Math.ceil((total || 0) / pageSize),
      };
    } catch (error) {
      console.error("❌ Error in getPharmacistAssignmentLogs:", error);
      return { success: false, error: error.message };
    }
  }
}
